import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import {
  X, Loader, AlertTriangle, Check, CheckCircle2, RotateCcw, ShieldAlert, Info
} from 'lucide-react';
import TexteAccentue from './TexteAccentue';
import { elementActionnable } from './elementActionnable';
import ChoixCategorieGoogle from './ChoixCategorieGoogle';

/**
 * Les formats d'annonce, en français.
 *
 * ⚠️ Un format inconnu est rendu tel quel plutôt que masqué : c'est laid, mais exact, et celui
 * qui le lit saura de quoi il s'agit. Le taire laisserait croire à une annonce sans format.
 */
const FORMATS = {
  RESPONSIVE_SEARCH_AD: 'annonce responsive',
  EXPANDED_TEXT_AD: 'annonce textuelle étendue',
  TEXT_AD: 'annonce textuelle',
  RESPONSIVE_DISPLAY_AD: 'annonce display responsive',
  APP_AD: "annonce d'application",
  CALL_AD: "annonce avec appel",
  SMART_CAMPAIGN_AD: 'annonce de campagne intelligente'
};
const nommerFormat = (type) => (type ? (FORMATS[type] || type) : 'format inconnu');

/** L'étape qu'ouvre chaque famille de saisie — l'aperçu y revient quand on le quitte. */
const ETAPE_DE_SAISIE = {
  accroches: 'accroches',
  sitelinks: 'accroches',
  categorie: 'categorie',
  annonce: 'annonce',
  analyse: 'analyse',
  url: 'url',
  isolement: 'isolement',
  rsa: 'redaction'
};

/** Ce qu'on lit pendant que l'écran de saisie se charge. */
const ATTENTE = {
  accroches: 'Lecture des accroches du compte…',
  categorie: 'Lecture des produits du flux…',
  sitelinks: 'Lecture des liens annexes du compte…',
  annonce: "Lecture de l'annonce…",
  analyse: 'Analyse de la page de destination…',
  url: 'Lecture de la page de destination…',
  isolement: 'Lecture du groupe et de son annonce…'
};

/** Ce qu'annonce le bouton qui ouvre l'écran de saisie, par famille de saisie. */
const LIBELLE_OUVERTURE = {
  accroches: 'Choisir les accroches',
  categorie: 'Choisir la catégorie',
  sitelinks: 'Choisir les liens annexes',
  annonce: "Ouvrir l'annonce",
  analyse: 'Analyser la page',
  url: 'Choisir la page',
  isolement: 'Préparer le groupe',
  rsa: 'Rédiger'
};

/**
 * Corriger un constat dans la régie, depuis Kokpit.
 *
 * ## Le geste, en trois temps
 *
 * 1. **Sélection** — la liste des éléments fautifs, tout coché par défaut. Un doublon demande en
 *    plus de choisir *laquelle* de ses occurrences mettre en pause : l'outil n'a pas à trancher
 *    quelle campagne garde le mot-clé.
 * 2. **Aperçu** — le serveur relit l'état réel du compte et dit ce qu'il va écrire. Les éléments
 *    déjà réglés depuis le relevé sont écartés et annoncés comme tels, pas comme des erreurs :
 *    un constat vieux de trois jours peut avoir été traité à la main entre-temps.
 * 3. **Écriture**, puis le compte rendu — ce qui est passé, ce qui a échoué avec le motif de
 *    Google, et un bouton pour revenir en arrière tant que la modale est ouverte.
 *
 * Une action peut intercaler un quatrième temps, la **rédaction** (`action.saisie`) : créer une
 * annonce ne se déduit pas d'une cible, il faut en écrire le texte. Le serveur propose un
 * brouillon tiré du groupe — ses mots-clés, ses annonces en pause, son URL de destination —, et
 * l'utilisateur le corrige avant que l'aperçu ne reprenne son cours habituel.
 *
 * ⚠️ **Une rédaction se fait pour un groupe à la fois.** Rédiger vingt annonces d'un coup n'est
 * pas un geste de masse mais vingt textes différents à relire ; la sélection passe donc en
 * bouton radio, et le brouillon se charge pour l'élément choisi.
 *
 * ## Pourquoi un aperçu, et pas un bouton direct
 *
 * Parce que le constat est une photo. Écrire sans relire, c'est annoncer « 24 annonces mises en
 * pause » là où il n'en restait que trois à traiter — et faire douter l'utilisateur de tout ce
 * que l'écran lui montre par ailleurs.
 *
 * ⚠️ Rendue par un portail, comme toute modale du projet : posée dans l'arbre de la carte, elle
 * passerait sous la barre latérale.
 */
const ModaleCorrection = ({ reco, action: actionDemandee, token, origine = 'RADAR', elementUnique = null, onFermer, onCorrige }) => {
  /*
   * L'action peut changer en cours de route, et une seule fois : quand le texte d'une annonce
   * qu'on allait réactiver doit être corrigé alors que Google n'en autorise plus la
   * modification. Le geste bascule alors vers la création d'une annonce responsive, en
   * reprenant ce texte — c'est la migration que Google impose depuis juin 2022.
   *
   * ⚠️ **Les deux actions viennent du même constat** (`reco.actions`) : on ne fabrique rien, on
   * passe de l'une à l'autre. Une action inventée ici n'aurait ni relecture, ni annulation.
   */
  const [actionBasculee, setActionBasculee] = useState(null);
  const action = actionBasculee || actionDemandee;
  const [etape, setEtape] = useState('selection');
  const [selection, setSelection] = useState({});
  const [choix, setChoix] = useState({});
  const [apercu, setApercu] = useState(null);
  const [issue, setIssue] = useState(null);
  const [erreur, setErreur] = useState(null);
  const [occupe, setOccupe] = useState(false);
  /** Le brouillon en cours de rédaction, et le contexte que le serveur a lu pour l'établir. */
  const [brouillon, setBrouillon] = useState(null);
  const [contexteBrouillon, setContexteBrouillon] = useState(null);
  /**
   * Le détail des choix, lu dans la régie à l'ouverture — voir `action.choixDetaille`.
   *
   * Indexé par identifiant de groupe. ⚠️ **Jamais tiré du constat** : c'est ce contenu qu'on
   * propose de modifier, et réécrire par-dessus une photo périmée écraserait le travail d'un
   * autre.
   */
  const [detailChoix, setDetailChoix] = useState(null);
  const [choixCharge, setChoixCharge] = useState(false);
  /** Le contenu corrigé de l'annonce retenue, par ligne du constat. */
  const [edition, setEdition] = useState({});
  /** Les accroches du compte, et ce qu'on en retient — voir `saisie: 'accroches'`. */
  const [accroches, setAccroches] = useState(null);
  const [accrochesRetenues, setAccrochesRetenues] = useState({});
  const [nouvellesAccroches, setNouvellesAccroches] = useState(['']);
  /*
   * Les liens annexes rédigés : un **objet** par ligne, là où une accroche est une chaîne.
   * C'est la seule différence de fond entre les deux familles d'extension — le reste de l'écran
   * est commun.
   */
  const [nouveauxSitelinks, setNouveauxSitelinks] = useState([{ texte: '', description1: '', description2: '', url: '' }]);
  /**
   * L'annonce qu'on corrige : sa fiche lue à l'instant, et le contenu en cours d'édition.
   *
   * ⚠️ **Un état à part, et non une entrée d'`edition`.** Ici l'annonce *est* l'élément du
   * constat, là où dans la réactivation elle est un choix parmi les candidates d'un groupe. Les
   * mêler obligerait à porter un index qui ne veut pas la même chose dans les deux cas.
   */
  const [annonce, setAnnonce] = useState(null);
  /** L'analyse d'une page de destination — lecture seule, rien à écrire. */
  const [analyse, setAnalyse] = useState(null);
  /** La destination d'un mot-clé : celle qu'il porte, celle dont il hérite, et celle qu'on vise. */
  const [destination, setDestination] = useState(null);
  /** Le groupe dédié à créer : son nom, et l'annonce qui l'accompagne. */
  const [isolement, setIsolement] = useState(null);
  /** Les produits d'un flux à corriger, groupés par la taxonomie du marchand. */
  const [flux, setFlux] = useState(null);

  /*
   * Un élément désigné depuis la liste du constat réduit la modale à lui seul : on a cliqué sur
   * une ligne précise, pas sur le constat. Le reste du parcours est identique — aperçu compris,
   * parce que relire l'état réel vaut autant pour un élément que pour trente.
   */
  const elements = elementUnique ? [elementUnique] : (reco.affectedItems || []);
  const choixRequis = action.choixRequis;
  /*
   * L'action réclame un contenu composé ici — voir `CREER_ANNONCE` dans le registre. On ne coche
   * plus, on choisit **un** élément puis on rédige.
   */
  const saisie = action.saisie;
  /** D'où lire le détail des annonces candidates, quand le constat ne suffit pas à choisir. */
  const choixDetaille = action.choixDetaille;
  /**
   * A-t-on sauté l'étape de choix ?
   *
   * Une `ref` et non un état : elle ne change rien à l'affichage, elle empêche seulement le saut
   * de se rejouer à chaque rendu. Elle sert aussi à savoir où « Revenir » doit mener — vers une
   * étape qu'on n'a jamais montrée, ou vers la fermeture.
   */
  const saut = useRef(false);

  /**
   * Les objets à modifier, quand plusieurs lignes du constat désignent le même.
   *
   * ⚠️ **Cocher des lignes ne permet pas de choisir un objet.** Un négatif « paris » bloque
   * cinq mots-clés : pour ne retirer que lui, il faudrait repérer lesquelles des dix lignes
   * affichées le concernent — et deux d'entre elles peuvent être hors de l'aperçu plafonné.
   * La sélection porte donc sur l'objet, et les lignes qu'il débloque se lisent sous lui.
   *
   * Le serveur ne fournit ces groupes que s'il y a réellement regroupement : une liste à plat
   * se lit mieux quand une ligne vaut un objet.
   */
  const groupes = elementUnique ? null : (action.groupes || null);

  /** Les éléments que cette action peut réellement toucher. */
  const actionnables = elements.filter(e => elementActionnable(e, action));

  /**
   * L'étape de choix va-t-elle être sautée ?
   *
   * ⚠️ **Calculé au rendu, et pas seulement dans l'effet.** L'effet ne s'exécute qu'après la
   * peinture, et le chargement qu'il déclenche dure le temps d'un appel : l'étape de choix
   * s'affichait une demi-seconde avant de disparaître. Un écran qui apparaît pour être aussitôt
   * remplacé se lit comme un bug, même quand tout fonctionne.
   *
   * ⚠️ **Sauf si le chargement a échoué** : l'étape reprend alors sa place, avec son bouton,
   * pour qu'on puisse réessayer au lieu de rester devant un écran d'attente définitif.
   */
  /*
   * ⚠️ **Un geste qui porte sur le constat saute toujours l'étape de choix** : il n'y a rien à
   * choisir, et le constat n'a pas d'éléments. Sans cela, l'écran s'ouvrait sur « ce constat ne
   * porte pas les identifiants nécessaires » — un refus exact pour les gestes ordinaires, et
   * totalement trompeur ici, où les produits se demandent justement à l'écran suivant.
   */
  const sautImmediat = Boolean(saisie) && !erreur
    && (action.surLeConstat || actionnables.length === 1);

  useEffect(() => {
    // Tout coché d'emblée : l'utilisateur a ouvert la modale pour corriger, pas pour cocher.
    // Pour un doublon, rien n'est coché tant qu'aucune occurrence n'est choisie — cocher par
    // défaut reviendrait à décider à sa place laquelle mettre en pause.
    const depart = {};
    const lignes = groupes || actionnables;
    /*
     * Rien de coché d'emblée quand il faut choisir — une occurrence de doublon, ou le groupe
     * dont on va rédiger l'annonce : cocher reviendrait à décider à la place de l'utilisateur.
     *
     * Sauf s'il n'y a qu'une ligne : là il n'y a rien à arbitrer, et faire cliquer un bouton
     * radio sans alternative est une formalité, pas un choix. Cela vaut aussi quand la modale
     * est ouverte depuis une ligne précise du constat.
     */
    const choisiDOffice = !choixRequis && (!saisie || lignes.length === 1);
    lignes.forEach((_, i) => { depart[i] = choisiDOffice; });
    setSelection(depart);
    setBrouillon(null);
    setContexteBrouillon(null);
    setEdition({});
    setAccroches(null);
    setAccrochesRetenues({});
    setNouvellesAccroches(['']);
    setNouveauxSitelinks([{ texte: '', description1: '', description2: '', url: '' }]);
    setAnnonce(null);
    setAnalyse(null);
    setDestination(null);
    setIsolement(null);
    setFlux(null);
    saut.current = false;
    /*
   * ⚠️ **`actionDemandee`, et non `action`.** Ces effets remettent l'écran à zéro quand le
   * parent ouvre un autre geste. Les accrocher à `action` les faisait aussi se déclencher sur
   * une bascule interne — celle qui reprend le texte d'une annonce figée dans une nouvelle —,
   * effaçant le brouillon au moment même où il venait d'être rempli : l'écran de rédaction
   * s'ouvrait vide.
   */
}, [reco.id, actionDemandee.cle]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Charge le détail des choix dès l'ouverture.
   *
   * ⚠️ **Avant le choix, pas après.** Quatre annonces d'un groupe de marque portent toutes le
   * même premier titre : le bouton radio demandait un arbitrage sans donner de quoi arbitrer.
   * Le contenu doit être lisible au moment où l'on choisit, et modifiable dans la foulée.
   */
  useEffect(() => {
    if (!choixDetaille) return;

    /*
     * ⚠️ **Seules les annonces en pause se chargent à l'ouverture.** Le catalogue d'accroches,
     * lui, se charge au choix de la campagne (`demanderAccroches`) : c'est la campagne retenue
     * qui dit quelles accroches y sont **déjà posées**, et les charger d'avance rendrait cette
     * marque fausse dès qu'un constat porte plusieurs campagnes.
     */
    /*
     * ⚠️ **Toutes les saisies chargent au choix, pas à l'ouverture.** Un constat de cent huit
     * annonces demanderait cent huit lectures d'avance, dont une seule servira. Seule la
     * réactivation charge d'emblée : ses candidates doivent être lisibles *pour* choisir.
     */
    if (saisie) { setChoixCharge(true); return; }

    const groupes = [...new Set(elements.map(e => e.cible?.adGroupId).filter(Boolean))];
    if (groupes.length === 0) { setChoixCharge(true); return; }

    let abandonne = false;
    (async () => {
      try {
        const donnees = await appeler(choixDetaille, { accountId: reco.accountId, adGroupIds: groupes });
        if (!abandonne) setDetailChoix(donnees.parGroupe || {});
      } catch (e) {
        /*
         * Une lecture qui échoue ne ferme pas le geste : on retombe sur les libellés du constat,
         * sans possibilité de modifier. Mieux vaut un choix pauvre qu'un écran bloqué — et
         * l'absence de champs dit d'elle-même qu'on n'a pas lu l'annonce.
         */
        if (!abandonne) setErreur(e.message);
      } finally {
        if (!abandonne) setChoixCharge(true);
      }
    })();
    return () => { abandonne = true; };
    /*
   * ⚠️ **`actionDemandee`, et non `action`.** Ces effets remettent l'écran à zéro quand le
   * parent ouvre un autre geste. Les accrocher à `action` les faisait aussi se déclencher sur
   * une bascule interne — celle qui reprend le texte d'une annonce figée dans une nouvelle —,
   * effaçant le brouillon au moment même où il venait d'être rempli : l'écran de rédaction
   * s'ouvrait vide.
   */
}, [reco.id, actionDemandee.cle]); // eslint-disable-line react-hooks/exhaustive-deps

  /** Fermer par Échap : une modale qui ne se ferme qu'au clic piège le clavier. */
  useEffect(() => {
    const auClavier = (e) => { if (e.key === 'Escape' && !occupe) onFermer(); };
    window.addEventListener('keydown', auClavier);
    return () => window.removeEventListener('keydown', auClavier);
  }, [onFermer, occupe]);

  /**
   * Les annonces candidates d'une ligne du constat.
   *
   * Celles relues dans la régie quand la lecture a abouti ; à défaut, celles du constat — qui ne
   * portent qu'un libellé, et ne se modifient donc pas.
   */
  const candidatesDe = (element) => {
    const fraiches = detailChoix?.[element.cible?.adGroupId]?.annonces;
    return fraiches || element.cible?.[choixRequis] || [];
  };

  /**
   * Les identifiants que porte une occurrence choisie.
   *
   * ⚠️ **Une occurrence désigne l'objet à modifier, et chaque action en désigne un autre** : une
   * occurrence de doublon est un mot-clé dans une campagne, une annonce en pause est une annonce
   * dans son groupe. Les champs voulus se lisent donc dans `action.champsCible`, plus
   * `campaignId` qui sert au journal — et non dans une liste écrite ici, qui ne connaissait que
   * le vocabulaire des mots-clés et aurait perdu `adId` en silence.
   *
   * `keywordId` est accepté comme `criterionId` : c'est le nom que `DuplicateKeywordCheck` donne
   * au sien, et les constats enregistrés avant cette version le portent encore.
   */
  const ciblerOccurrence = useCallback((occurrence) => {
    const retenue = {};
    for (const champ of [...(action.champsCible || []), 'campaignId']) {
      const valeur = occurrence[champ] ?? (champ === 'criterionId' ? occurrence.keywordId : undefined);
      if (valeur !== undefined && valeur !== null) retenue[champ] = valeur;
    }
    return retenue;
  }, [action.champsCible]);

  /** Les cibles choisies, dans le vocabulaire que le serveur attend. */
  const ciblesChoisies = useCallback(() => (groupes
    // Un groupe coché vaut sa cible, une seule fois : c'est le serveur qui dédoublonne, mais
    // envoyer cinq fois la même ferait un aperçu incompréhensible avant même qu'il n'agisse.
    ? groupes.filter((_, index) => selection[index]).map(g => g.cible)
    : actionnables
    .map((element, index) => {
      if (!selection[index]) return null;

      /*
       * Le contenu rédigé voyage **avec** la cible : le serveur n'invente rien, il reçoit la
       * cible du constat augmentée de ce que l'écran a composé. C'est aussi ce qui finit dans
       * le journal, donc ce qu'on relira pour savoir ce qui est parti.
       */
      if (saisie === 'url') {
        if (!destination || destination.element !== element) return null;
        const voulue = String(destination.voulue || '').trim();
        // ⚠️ Vide vaut « revenir à l'héritage de l'annonce » : c'est `null`, pas une absence.
        return { ...ciblerOccurrence(element.cible), finalUrl: voulue || null };
      }

      if (saisie === 'isolement') {
        if (!isolement || isolement.element !== element) return null;
        return {
          ...ciblerOccurrence(element.cible),
          motCle: element.cible.motCle,
          ciblage: element.cible.ciblage || 'EXACT',
          nomGroupe: isolement.nomGroupe,
          finalUrl: isolement.finalUrl,
          headlines: isolement.headlines.map(t => t.trim()).filter(Boolean),
          descriptions: isolement.descriptions.map(t => t.trim()).filter(Boolean),
          path1: isolement.path1,
          path2: isolement.path2,
          cpcBidMicros: isolement.contexte?.cpcBidMicros || null
        };
      }

      if (saisie === 'annonce') {
        if (!annonce || annonce.element !== element) return null;
        const contenu = annonce.contenus[annonce.adId];
        if (!contenu) return null;
        return {
          ...ciblerOccurrence(element.cible),
          // ⚠️ L'annonce **retenue**, et non celle du constat : un constat de mot-clé n'en
          // nomme aucune, et c'est l'écran qui l'a choisie parmi celles du groupe.
          adId: annonce.adId,
          headlines: contenu.headlines,
          descriptions: contenu.descriptions,
          path1: contenu.path1,
          path2: contenu.path2
        };
      }

      if (saisie === 'accroches' || saisie === 'sitelinks') {
        const existantes = (accroches || [])
          .filter(a => accrochesRetenues[a.resourceName])
          .map(a => a.resourceName);
        const redigees = saisie === 'sitelinks' ? sitelinksValides() : nouvellesAccroches.map(t => t.trim()).filter(Boolean);
        if (existantes.length === 0 && redigees.length === 0) return null;
        return {
          ...ciblerOccurrence(element.cible),
          extensionsExistantes: existantes,
          ...(saisie === 'sitelinks' ? { nouveauxSitelinks: redigees } : { nouvellesAccroches: redigees })
        };
      }

      if (saisie) {
        if (!brouillon) return null;
        return {
          // Les identifiants seuls, et non toute la cible : celle d'un groupe porte la liste de
          // ses annonces en pause, qui n'a rien à faire dans une création — ni sur le réseau, ni
          // dans le journal que l'on relira pour savoir ce qui est parti.
          ...ciblerOccurrence(element.cible),
          finalUrl: brouillon.finalUrl,
          headlines: brouillon.headlines,
          descriptions: brouillon.descriptions,
          path1: brouillon.path1 || null,
          path2: brouillon.path2 || null
        };
      }

      if (!choixRequis) return element.cible;

      const retenue = candidatesDe(element)[choix[index]];
      if (!retenue) return null;

      /*
       * Le contenu corrigé voyage avec les identifiants. ⚠️ **Il n'est joint que si l'annonce a
       * été relue** : le constat ne porte pas de texte, et envoyer une liste vide
       * *supprimerait* les messages de l'annonce au lieu de la laisser intacte.
       */
      const corrige = edition[index];
      return corrige
        ? { ...ciblerOccurrence(retenue), ...corrige }
        : ciblerOccurrence(retenue);
    })
    .filter(Boolean)), // eslint-disable-next-line react-hooks/exhaustive-deps
  [actionnables, groupes, selection, choix, choixRequis, saisie, brouillon, edition, detailChoix,
    accroches, accrochesRetenues, nouvellesAccroches, nouveauxSitelinks, annonce,
    destination, isolement, ciblerOccurrence]);

  const appeler = async (chemin, corps) => {
    const reponse = await fetch(chemin, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        // Déclaratif, et n'autorise rien : sert au journal, pour qu'on sache d'où la correction
        // a été lancée — le feed, ou l'écran d'un compte.
        'X-Kokpit-Origine': origine
      },
      body: JSON.stringify(corps)
    });
    const donnees = await reponse.json();
    if (!reponse.ok || !donnees.success) {
      const e = new Error(donnees.error || 'Échec');
      e.code = donnees.code;
      throw e;
    }
    return donnees;
  };

  const corps = () => ({
    tool: reco.tool,
    accountId: reco.accountId,
    checkKey: reco.checkKey,
    action: action.cle,
    statusObserve: reco.status,
    cibles: ciblesChoisies()
  });

  /**
   * Demande au serveur un brouillon pour le groupe choisi, et passe à la rédaction.
   *
   * ⚠️ **Un brouillon n'est pas une écriture.** Cette route lit le compte et propose un texte ;
   * rien ne part chez Google avant l'aperçu puis la confirmation, comme pour toute autre action.
   */
  const demanderBrouillon = async (elementChoisi = null) => {
    /*
     * ⚠️ L'élément peut être **passé** plutôt que relu dans la sélection : le saut automatique
     * se déclenche au premier rendu, avant que `setSelection` n'ait été appliqué. Le relire là
     * ne trouverait rien, et l'écran resterait sur une étape qui n'a rien à faire choisir.
     */
    const choisi = elementChoisi || actionnables.find((_, index) => selection[index]);
    if (!choisi) return;
    setOccupe(true); setErreur(null);
    try {
      const donnees = await appeler('/api/actions/brouillon-annonce', {
        accountId: reco.accountId,
        adGroupId: choisi.cible.adGroupId
      });
      setBrouillon(donnees.brouillon);
      setContexteBrouillon({ ...donnees.contexte, limites: donnees.limites, element: choisi });
      setEtape('redaction');
    } catch (e) { setErreur(e.message); } finally { setOccupe(false); }
  };

  /**
   * Retient une annonce candidate, et en prend une copie modifiable.
   *
   * La copie est faite au choix, et non à l'affichage : on ne modifie que celle qu'on réactive,
   * et garder quatre brouillons en parallèle laisserait croire qu'ils partiront tous.
   */
  const retenirAnnonce = (index, rang) => {
    const annonce = candidatesDe(actionnables[index])[rang];
    setChoix(p => ({ ...p, [index]: rang }));
    setSelection(p => ({ ...p, [index]: true }));
    setEdition(p => ({
      ...p,
      // Une annonce qu'on n'a pas relue, ou d'un type qui ne se réécrit pas, n'est pas éditable :
      // pas de copie, donc rien ne sera joint à la cible.
      [index]: annonce?.modifiable ? {
        headlines: (annonce.headlines || []).map(a => ({ ...a })),
        descriptions: (annonce.descriptions || []).map(a => ({ ...a })),
        path1: annonce.path1 || null,
        path2: annonce.path2 || null
      } : undefined
    }));
  };

  /**
   * Reprend le texte d'une annonce qu'on ne peut plus modifier, dans une nouvelle annonce
   * responsive.
   *
   * ## Pourquoi ce détour
   *
   * Les annonces textuelles étendues ne se modifient plus — ni par l'API, ni dans l'interface
   * Google. Proposer des champs éditables serait une promesse fausse ; renvoyer l'utilisateur
   * rédiger de zéro lui ferait recopier à la main un texte qu'on a sous les yeux.
   *
   * Le geste juste est donc celui que Google impose depuis juin 2022 : **reprendre le texte dans
   * une annonce responsive**. Le groupe rediffuse avec un contenu corrigé, dans le seul format
   * que la régie accepte encore.
   *
   * ⚠️ **L'ancienne annonce reste en pause.** On ne la réactive pas : ce serait remettre en
   * diffusion le texte qu'on vient justement de vouloir changer.
   */
  const reprendreDansUneNouvelle = (index, occurrence) => {
    const creation = (reco.actions || []).find(a => a.saisie === 'rsa');
    if (!creation) return;

    setActionBasculee(creation);
    setSelection({ [index]: true });
    setBrouillon({
      // Le rédacteur travaille sur du texte simple : l'épinglage d'une annonce qui disparaît
      // n'a pas à être transposé sur une autre, qui n'aura pas les mêmes positions.
      headlines: (occurrence.headlines || []).map(a => a.text),
      descriptions: (occurrence.descriptions || []).map(a => a.text),
      path1: occurrence.path1 || null,
      path2: occurrence.path2 || null,
      finalUrl: occurrence.finalUrl || null,
      redigePar: 'reprise'
    });
    setContexteBrouillon({
      element: actionnables[index],
      urlDeduite: Boolean(occurrence.finalUrl),
      reprisDe: occurrence.libelle,
      formatRepris: occurrence.type,
      limites: { titre: 30, description: 90, chemin: 15, titresMin: 3, titresMax: 15, descriptionsMin: 2, descriptionsMax: 4 }
    });
    setErreur(null);
    setEtape('redaction');
  };

  /** Modifier un message de l'annonce retenue, à la frappe. */
  const changerMessage = (index, champ, rang, valeur) => setEdition(p => {
    const copie = p[index];
    if (!copie) return p;
    const liste = [...(copie[champ] || [])];
    liste[rang] = { ...liste[rang], text: valeur };
    return { ...p, [index]: { ...copie, [champ]: liste } };
  });

  /**
   * Les liens annexes rédigés qui portent quelque chose.
   *
   * ⚠️ **Une ligne à moitié remplie n'est pas une ligne vide.** Un libellé sans URL est une
   * intention, et la laisser filer ferait refuser toute la requête par Google — on la retient
   * donc, pour que la validation la nomme.
   */
  const sitelinksValides = () => nouveauxSitelinks
    .map(l => ({
      texte: (l.texte || '').trim(),
      description1: (l.description1 || '').trim(),
      description2: (l.description2 || '').trim(),
      url: (l.url || '').trim()
    }))
    .filter(l => l.texte || l.url || l.description1 || l.description2);

  const changerSitelink = (rang, champ, valeur) => setNouveauxSitelinks(liste => {
    const copie = [...liste];
    copie[rang] = { ...copie[rang], [champ]: valeur };
    return copie;
  });

  /**
   * Charge le catalogue d'accroches pour la campagne retenue, et ouvre le sélecteur.
   *
   * ⚠️ **Lu pour cette campagne-là**, parce que la réponse marque ce qui y est déjà posé :
   * proposer une accroche déjà rattachée ferait échouer l'écriture sur un doublon, avec un
   * message de Google que personne ne saurait relier au geste.
   */
  const demanderAccroches = async (elementChoisi = null) => {
    // Voir `demanderBrouillon` : l'élément peut être passé, pour le saut automatique.
    const choisi = elementChoisi || actionnables.find((_, index) => selection[index]);
    if (!choisi) return;
    setOccupe(true); setErreur(null);
    try {
      const donnees = await appeler(choixDetaille, {
        accountId: reco.accountId,
        campaignId: choisi.cible.campaignId
      });
      setAccroches(donnees.accroches || []);
      setContexteBrouillon({ limites: donnees.limites || { texte: 25, recommande: 4 }, element: choisi });
      setAccrochesRetenues({});
      setNouvellesAccroches(['']);
      setNouveauxSitelinks([{ texte: '', description1: '', description2: '', url: '' }]);
      setEtape('accroches');
    } catch (e) { setErreur(e.message); } finally { setOccupe(false); }
  };

  /**
   * Ouvre directement l'écran de saisie quand il n'y a **rien à choisir**.
   *
   * L'étape de sélection existe pour trancher entre plusieurs campagnes. Quand le constat n'en
   * porte qu'une, ou quand on a cliqué « Ajouter » au bout d'une ligne précise, elle se réduit à
   * un bouton radio sans alternative et à un clic de plus : une formalité, pas un choix.
   *
   * ⚠️ **L'élément est passé à la fonction**, et non relu dans la sélection : au premier rendu,
   * `setSelection` n'a pas encore été appliqué.
   */
  useEffect(() => {
    if (!saisie || saut.current || etape !== 'selection' || occupe) return;
    if (!action.surLeConstat && actionnables.length !== 1) return;
    saut.current = true;
    ouvrirLaSaisie(action.surLeConstat ? null : actionnables[0]);
  }, [saisie, etape, actionnables.length, action.surLeConstat]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Lit l'annonce retenue dans la régie, et ouvre l'écran de correction.
   *
   * ⚠️ **Lue à l'instant, jamais tirée du constat** : c'est ce texte qu'on va réécrire, et
   * réécrire par-dessus une photo vieille de plusieurs mois écraserait le travail d'un autre.
   */
  /** Le contenu d'une annonce, dans la forme que l'écran édite. */
  const contenuDe = (fiche) => ({
    headlines: (fiche.headlines || []).map(a => ({ ...a })),
    descriptions: (fiche.descriptions || []).map(a => ({ ...a })),
    path1: fiche.path1 || null,
    path2: fiche.path2 || null
  });

  const demanderAnnonce = async (elementChoisi = null) => {
    const choisi = elementChoisi || actionnables.find((_, index) => selection[index]);
    if (!choisi) return;
    setOccupe(true); setErreur(null);
    try {
      /*
       * ⚠️ **Deux façons de désigner ce qu'on ouvre, et le constat décide.**
       *
       * Un constat d'annonce de mauvaise qualité nomme l'annonce ; un constat de mot-clé à
       * faible Quality Score ne nomme que son **groupe** — c'est l'annonce du groupe qu'on vient
       * corriger, et le mot-clé n'en a pas une à lui. Envoyer `adIds: [undefined]` rendait
       * « Aucun groupe d'annonces ni annonce valide », une erreur qui ne disait rien de la cause.
       */
      const parAnnonce = Boolean(choisi.cible.adId);
      const donnees = await appeler(choixDetaille, parAnnonce
        ? { accountId: reco.accountId, adIds: [choisi.cible.adId] }
        : { accountId: reco.accountId, adGroupIds: [choisi.cible.adGroupId] });

      const trouvees = Object.values(donnees.parGroupe || {}).flatMap(g => g.annonces || []);
      const fiches = parAnnonce
        ? trouvees.filter(a => String(a.adId) === String(choisi.cible.adId))
        : trouvees;

      if (fiches.length === 0) {
        throw new Error(parAnnonce
          ? "Cette annonce n'a pas été retrouvée dans le compte : relancez le relevé."
          : "Ce groupe n'a aucune annonce active : il n'y a rien à corriger ici.");
      }

      // Seules les responsives se réécrivent. Les autres formats sont nommés plutôt que tus.
      const modifiables = fiches.filter(a => a.modifiable);
      if (modifiables.length === 0) {
        const formats = [...new Set(fiches.map(a => nommerFormat(a.type)))].join(', ');
        throw new Error(`Aucune annonce de ce groupe n'est modifiable : Google n'autorise plus la modification des formats présents (${formats}).`);
      }

      const premiere = modifiables[0];
      setAnnonce({
        element: choisi,
        fiches: modifiables,
        adId: premiere.adId,
        fiche: premiere,
        // Les contenus sont gardés **par annonce** : passer de l'une à l'autre pour comparer ne
        // doit pas effacer ce qu'on vient d'y écrire.
        contenus: Object.fromEntries(modifiables.map(a => [a.adId, contenuDe(a)]))
      });
      setEtape('annonce');
    } catch (e) { setErreur(e.message); } finally { setOccupe(false); }
  };

  /**
   * Analyse la page de destination du mot-clé retenu — **sans rien écrire**.
   *
   * ⚠️ L'écran ne connaît pas l'URL : le constat porte un mot-clé, et sa destination est le plus
   * souvent héritée de l'annonce. C'est la route qui la résout, pour éviter deux allers-retours.
   */
  const demanderAnalyse = async (elementChoisi = null) => {
    const choisi = elementChoisi || actionnables.find((_, index) => selection[index]);
    if (!choisi) return;
    setOccupe(true); setErreur(null);
    try {
      const donnees = await appeler(choixDetaille, {
        accountId: reco.accountId,
        adGroupId: choisi.cible.adGroupId,
        criterionId: choisi.cible.criterionId,
        motCle: choisi.cible.motCle || null
      });
      setAnalyse({ element: choisi, ...donnees.analyse, destination: donnees.destination || null });
      setEtape('analyse');
    } catch (e) { setErreur(e.message); } finally { setOccupe(false); }
  };

  /** Lit la destination courante du mot-clé, et ouvre le champ. */
  const demanderDestination = async (elementChoisi = null) => {
    const choisi = elementChoisi || actionnables.find((_, index) => selection[index]);
    if (!choisi) return;
    setOccupe(true); setErreur(null);
    try {
      const donnees = await appeler(choixDetaille, {
        accountId: reco.accountId,
        adGroupId: choisi.cible.adGroupId,
        criterionId: choisi.cible.criterionId
      });
      setDestination({
        element: choisi,
        ...donnees.destination,
        // Pré-rempli avec la destination actuelle : on part de ce qui s'applique, et on corrige.
        voulue: donnees.destination?.courante || ''
      });
      setEtape('url');
    } catch (e) { setErreur(e.message); } finally { setOccupe(false); }
  };

  /** Lit le contexte du groupe, et prépare le groupe dédié à créer. */
  const demanderIsolement = async (elementChoisi = null) => {
    const choisi = elementChoisi || actionnables.find((_, index) => selection[index]);
    if (!choisi) return;
    setOccupe(true); setErreur(null);
    try {
      const donnees = await appeler(choixDetaille, {
        accountId: reco.accountId,
        campaignId: choisi.cible.campaignId,
        adGroupId: choisi.cible.adGroupId
      });
      const contexte = donnees.contexte || {};
      const modele = contexte.modele;
      if (!modele) {
        throw new Error("Ce groupe n'a aucune annonce responsive active dont partir : créez-en une d'abord.");
      }
      const motCle = choisi.cible.motCle || '';
      /*
       * ⚠️ **Le mot-clé passe en tête des titres.** C'est tout l'objet du découpage : une annonce
       * qui reprend le mot-clé. Les titres du modèle suivent, dédoublonnés — un titre qui serait
       * déjà le mot-clé ferait un doublon, que Google refuse.
       */
      const titreMotCle = motCle.slice(0, 30);
      const titres = [titreMotCle, ...modele.headlines.filter(
        t => t.trim().toLowerCase() !== titreMotCle.trim().toLowerCase()
      )].slice(0, 15);

      setIsolement({
        element: choisi,
        contexte,
        nomGroupe: motCle ? motCle.slice(0, 120) : `${contexte.adGroupName} — dédié`,
        finalUrl: modele.finalUrl || '',
        headlines: titres,
        descriptions: [...modele.descriptions].slice(0, 4),
        path1: modele.path1 || null,
        path2: modele.path2 || null
      });
      setEtape('isolement');
    } catch (e) { setErreur(e.message); } finally { setOccupe(false); }
  };

  const changerIsolement = (champ, valeur) => setIsolement(a => (a ? { ...a, [champ]: valeur } : a));

  /**
   * Charge les produits du flux qui portent ce problème, groupés par la taxonomie du marchand.
   *
   * ⚠️ **Le constat ne les porte pas** : Radar ne charge jamais les produits d'une analyse — ils
   * pèsent plusieurs méga-octets. C'est donc ici, et seulement quand le geste s'ouvre, qu'on
   * demande lesquels sont concernés.
   */
  const demanderFlux = async () => {
    setOccupe(true); setErreur(null);
    try {
      const donnees = await appeler(choixDetaille, {
        accountId: reco.accountId,
        checkKey: reco.checkKey
      });
      setFlux({
        ...donnees.flux,
        attribut: donnees.attribut,
        groupes: donnees.groupes || [],
        // Rien n'est coché d'emblée : affecter une catégorie à deux mille produits sans l'avoir
        // demandé serait exactement le geste qu'on veut rendre délibéré.
        retenus: {},
        categorie: null
      });
      setEtape('categorie');
    } catch (e) { setErreur(e.message); } finally { setOccupe(false); }
  };

  /** Les produits que les groupes cochés représentent. */
  const produitsRetenus = () => {
    if (!flux) return { offerIds: [], total: 0 };
    const groupes = flux.groupes.filter((_, index) => flux.retenus[index]);
    return {
      offerIds: groupes.flatMap(g => g.produits.map(p => p.offerId)),
      total: groupes.reduce((somme, g) => somme + g.total, 0),
      apercu: groupes.reduce((somme, g) => somme + g.produits.length, 0)
    };
  };

  /**
   * Ouvre l'écran de saisie que l'action réclame.
   *
   * ⚠️ **Un seul aiguillage, et non un ternaire recopié à trois endroits** — le bouton, le saut
   * automatique, et demain le suivant. Les trois avaient déjà commencé à diverger : le saut
   * ignorait les saisies ajoutées après lui, et l'écran restait sur une étape qui n'avait rien à
   * faire choisir.
   */
  const ouvrirLaSaisie = (elementChoisi = null) => {
    if (saisie === 'accroches' || saisie === 'sitelinks') return demanderAccroches(elementChoisi);
    if (saisie === 'annonce') return demanderAnnonce(elementChoisi);
    if (saisie === 'analyse') return demanderAnalyse(elementChoisi);
    if (saisie === 'url') return demanderDestination(elementChoisi);
    if (saisie === 'isolement') return demanderIsolement(elementChoisi);
    if (saisie === 'categorie') return demanderFlux();
    if (saisie) return demanderBrouillon(elementChoisi);
    return demanderApercu();
  };

  const changerTitreIsolement = (champ, rang, valeur) => setIsolement(a => {
    if (!a) return a;
    const liste = [...a[champ]];
    liste[rang] = valeur;
    return { ...a, [champ]: liste };
  });

  /** Le contenu de l'annonce en cours d'édition. */
  const contenuAnnonce = annonce ? annonce.contenus[annonce.adId] : null;

  /** Retient une autre annonce du groupe, sans perdre ce qui a été écrit sur la précédente. */
  const retenirAnnonceDuGroupe = (adId) => setAnnonce(a => (a
    ? { ...a, adId, fiche: a.fiches.find(f => String(f.adId) === String(adId)) || a.fiche }
    : a));

  const majContenu = (transformer) => setAnnonce(a => (a
    ? { ...a, contenus: { ...a.contenus, [a.adId]: transformer(a.contenus[a.adId]) } }
    : a));

  /** Modifier un message de l'annonce corrigée, à la frappe. */
  const changerMessageAnnonce = (champ, rang, valeur) => majContenu(contenu => {
    const liste = [...(contenu[champ] || [])];
    liste[rang] = { ...liste[rang], text: valeur };
    return { ...contenu, [champ]: liste };
  });

  const ajouterMessageAnnonce = (champ) => majContenu(contenu => ({
    ...contenu,
    [champ]: [...(contenu[champ] || []), { text: '', pinnedField: null }]
  }));

  const retirerMessageAnnonce = (champ, rang) => majContenu(contenu => ({
    ...contenu,
    [champ]: (contenu[champ] || []).filter((_, i) => i !== rang)
  }));

  const changerNouvelleAccroche = (rang, valeur) => setNouvellesAccroches(liste => {
    const copie = [...liste];
    copie[rang] = valeur;
    return copie;
  });

  /** Modifier un titre ou une description du brouillon, à la frappe. */
  const changerTexte = (champ, rang, valeur) => setBrouillon(actuel => {
    const liste = [...(actuel[champ] || [])];
    liste[rang] = valeur;
    return { ...actuel, [champ]: liste };
  });

  const retirerTexte = (champ, rang) => setBrouillon(actuel => ({
    ...actuel,
    [champ]: (actuel[champ] || []).filter((_, i) => i !== rang)
  }));

  const ajouterTexte = (champ) => setBrouillon(actuel => ({
    ...actuel,
    [champ]: [...(actuel[champ] || []), '']
  }));

  /*
   * ⚠️ **Les corrections de flux n'empruntent pas le chemin des régies.** Elles n'écrivent pas
   * chez Google : elles rangent une valeur dans l'Optimiseur, qui la ressort dans le flux
   * supplémentaire. Il n'y a donc ni relecture GAQL, ni aperçu serveur à demander — l'écran sait
   * déjà exactement ce qu'il va écrire, et sur combien de produits.
   */
  const ecrireFlux = async () => {
    setOccupe(true); setErreur(null);
    try {
      const resultat = await appeler(action.cheminPropre, {
        accountId: reco.accountId,
        checkKey: reco.checkKey,
        /*
         * ⚠️ **On envoie les groupes, pas les identifiants.** L'aperçu est borné à vingt
         * produits par groupe ; transmettre cette liste ferait corriger vingt produits là où
         * l'utilisateur en a coché cent soixante-onze. Le serveur relit le groupe entier.
         *
         * `null` désigne le groupe des produits sans taxonomie de marchand — une valeur, pas une
         * absence : `filter(Boolean)` l'aurait fait disparaître, et ce groupe est souvent le seul.
         */
        groupes: flux.groupes
          .map((g, index) => (flux.retenus[index] ? g.typeProduit : undefined))
          .filter(t => t !== undefined),
        valeur: flux.categorie.id
      });
      setIssue({ ...resultat, corrigees: Array.from({ length: resultat.corrigees || 0 }, (_, i) => String(i)) });
      setEtape('fait');
      if ((resultat.corrigees || 0) > 0) onCorrige(reco, resultat);
    } catch (e) { setErreur(e.message); setOccupe(false); }
  };

  const demanderApercu = async () => {
    setOccupe(true); setErreur(null);
    try {
      setApercu(await appeler('/api/actions/apercu', corps()));
      setEtape('apercu');
    } catch (e) { setErreur(e.message); } finally { setOccupe(false); }
  };

  const ecrire = async () => {
    setOccupe(true); setErreur(null);
    try {
      const resultat = await appeler('/api/actions', corps());
      setIssue(resultat);
      setEtape('fait');
      // Le parent retire la carte : le constat vient d'être rejoué, la ligne n'a plus d'objet.
      // Sauf si rien n'a été écrit — tout était déjà réglé —, auquel cas le rejeu s'en charge
      // aussi, mais il n'y a aucune correction à revendiquer.
      if ((resultat.corrigees || []).length > 0) onCorrige(reco, resultat);
    } catch (e) { setErreur(e.message); setOccupe(false); }
  };

  const annuler = async () => {
    setOccupe(true); setErreur(null);
    try {
      /*
       * ⚠️ **Une correction de flux se défait par son propre chemin.** La route des régies
       * reconstitue des mutations Google Ads depuis le registre ; ce geste n'en a aucune, et
       * son état d'avant vit produit par produit dans le journal.
       */
      const chemin = action.cheminPropre
        ? `/api/actions/flux/${issue.journalId}/annuler`
        : `/api/actions/${issue.journalId}/annuler`;
      const resultat = await appeler(chemin, {});
      setIssue({ ...issue, annule: resultat });
      setEtape('annule');
    } catch (e) { setErreur(e.message); } finally { setOccupe(false); }
  };

  const choisies = ciblesChoisies();

  /**
   * Combien d'**objets** seront modifiés, et combien de lignes du constat cela règle.
   *
   * ⚠️ Les deux diffèrent dès qu'un même objet bloque plusieurs lignes : un négatif large
   * « light tower » bloque cinq mots-clés, et le constat affiche cinq lignes pour un seul
   * retrait. Annoncer cinq gestes serait faux, et le compte rendu paraîtrait en échec quand
   * quatre des cinq écritures seraient refusées comme doublons.
   *
   * Le regroupement se fait sur les champs que l'action déclare — ceux-là mêmes qui composent
   * l'identité de la cible côté serveur.
   */
  const identite = (cible) => action.champsCible.map(c => cible?.[c]).join('~');
  const nombreCibles = new Set(choisies.map(identite)).size;
  const nombreChoisi = choisies.length;

  /**
   * Ce qui manque au brouillon pour pouvoir partir.
   *
   * Les bornes viennent du serveur (`limites`), qui les tient de Google. Les répéter ici en dur
   * les ferait diverger au premier changement — et un formulaire qui accepte ce que l'API refuse
   * fait échouer l'écriture après la confirmation, c'est-à-dire au pire moment.
   */
  const manques = (() => {
    /*
     * ⚠️ **Rien à valider tant qu'on choisit l'élément.** Un geste à saisie n'a pas encore de
     * contenu à l'étape de sélection : y appliquer sa toise ferait manquer « au moins une
     * accroche » d'emblée, et désarmerait le bouton qui mène justement à l'écran où on les
     * choisit. La saisie se valide dans son propre écran.
     */
    if (etape === 'selection' && saisie) return [];

    if (saisie === 'categorie') {
      if (!flux) return [];
      const liste = [];
      if (!flux.categorie?.id) liste.push('une catégorie Google');
      if (produitsRetenus().total === 0) liste.push('au moins un groupe de produits');
      return liste;
    }

    if (saisie === 'url') {
      if (!destination) return [];
      const voulue = String(destination.voulue || '').trim();
      const liste = [];
      // ⚠️ Un champ vide est **permis** : il rend la main à l'annonce. Ce n'est pas une erreur,
      // c'est le geste inverse — et l'écran le dit plutôt que de bloquer.
      if (voulue && !/^https?:\/\//i.test(voulue)) liste.push("l'URL doit commencer par http:// ou https://");
      if (voulue && voulue === destination.courante) liste.push("cette page est déjà celle qui s'applique");
      return liste;
    }

    if (saisie === 'isolement') {
      if (!isolement) return [];
      const titres = isolement.headlines.map(t => t.trim()).filter(Boolean);
      const descriptions = isolement.descriptions.map(t => t.trim()).filter(Boolean);
      const liste = [];
      if (!String(isolement.nomGroupe || '').trim()) liste.push('un nom pour le nouveau groupe');
      if (!/^https?:\/\//i.test(String(isolement.finalUrl || ''))) liste.push('une URL de destination valide');
      if (titres.length < 3) liste.push(`il faut 3 titres au minimum, il n'en reste que ${titres.length}`);
      if (descriptions.length < 2) liste.push(`il faut 2 descriptions au minimum, il n'en reste que ${descriptions.length}`);
      const longs = titres.filter(t => t.length > 30).length;
      if (longs) liste.push(`${longs} titre${longs > 1 ? 's dépassent' : ' dépasse'} 30 caractères`);
      const longues = descriptions.filter(d => d.length > 90).length;
      if (longues) liste.push(`${longues} description${longues > 1 ? 's dépassent' : ' dépasse'} 90 caractères`);
      const doublons = titres.filter((t, i) => titres.findIndex(a => a.toLowerCase() === t.toLowerCase()) !== i).length;
      if (doublons) liste.push(`${doublons} titre${doublons > 1 ? 's sont en double' : ' est en double'}`);
      return liste;
    }

    if (saisie === 'annonce') {
      if (!annonce || !annonce.contenus[annonce.adId]) return [];
      const bornes = { titre: 30, description: 90, titresMin: 3, descriptionsMin: 2 };
      const courant = annonce.contenus[annonce.adId];
      const titres = courant.headlines.map(a => (a.text || '').trim()).filter(Boolean);
      const descriptions = courant.descriptions.map(a => (a.text || '').trim()).filter(Boolean);
      const liste = [];
      if (titres.length < bornes.titresMin) liste.push(`il faut ${bornes.titresMin} titres au minimum, il n'en reste que ${titres.length}`);
      if (descriptions.length < bornes.descriptionsMin) liste.push(`il faut ${bornes.descriptionsMin} descriptions au minimum, il n'en reste que ${descriptions.length}`);
      const titresLongs = titres.filter(t => t.length > bornes.titre).length;
      if (titresLongs) liste.push(`${titresLongs} titre${titresLongs > 1 ? 's dépassent' : ' dépasse'} ${bornes.titre} caractères`);
      const descriptionsLongues = descriptions.filter(d => d.length > bornes.description).length;
      if (descriptionsLongues) liste.push(`${descriptionsLongues} description${descriptionsLongues > 1 ? 's dépassent' : ' dépasse'} ${bornes.description} caractères`);
      /*
       * ⚠️ Google refuse deux messages identiques dans une même annonce — et la correction en
       * fabrique facilement, en dupliquant une ligne pour la retoucher.
       */
      const doublons = [titres, descriptions].flatMap(liste2 => liste2.filter(
        (t, i) => liste2.findIndex(autre => autre.toLowerCase() === t.toLowerCase()) !== i
      )).length;
      if (doublons) liste.push(`${doublons} message${doublons > 1 ? 's sont en double' : ' est en double'}`);
      return liste;
    }

    if (saisie === 'sitelinks') {
      const bornes = contexteBrouillon?.limites || { texte: 25, description: 35 };
      const retenus = (accroches || []).filter(a => accrochesRetenues[a.resourceName]).length;
      const rediges = sitelinksValides();
      const liste = [];
      if (retenus + rediges.length === 0) liste.push('au moins un lien annexe');
      // ⚠️ L'URL est obligatoire : un libellé sans destination ferait refuser toute la requête.
      const sansUrl = rediges.filter(l => l.texte && !l.url).length;
      if (sansUrl) liste.push(`${sansUrl} lien${sansUrl > 1 ? 's' : ''} sans URL de destination`);
      const sansTexte = rediges.filter(l => !l.texte && (l.url || l.description1 || l.description2)).length;
      if (sansTexte) liste.push(`${sansTexte} lien${sansTexte > 1 ? 's' : ''} sans libellé`);
      const longs = rediges.filter(l => l.texte.length > (bornes.texte || 25)).length;
      if (longs) liste.push(`${longs} libellé${longs > 1 ? 's dépassent' : ' dépasse'} ${bornes.texte || 25} caractères`);
      const descriptionsLongues = rediges.filter(l =>
        l.description1.length > (bornes.description || 35) || l.description2.length > (bornes.description || 35)
      ).length;
      if (descriptionsLongues) liste.push(`${descriptionsLongues} description${descriptionsLongues > 1 ? 's dépassent' : ' dépasse'} ${bornes.description || 35} caractères`);
      // Google refuse la seconde ligne sans la première.
      const secondeSeule = rediges.filter(l => l.description2 && !l.description1).length;
      if (secondeSeule) liste.push(`${secondeSeule} seconde${secondeSeule > 1 ? 's lignes' : ' ligne'} de description sans la première`);
      return liste;
    }

    if (saisie === 'accroches') {
      const limite = contexteBrouillon?.limites?.texte || 25;
      const nouvelles = nouvellesAccroches.map(t => t.trim()).filter(Boolean);
      const retenues = (accroches || []).filter(a => accrochesRetenues[a.resourceName]).length;
      const liste = [];
      if (retenues + nouvelles.length === 0) liste.push('au moins une accroche');
      const longues = nouvelles.filter(t => t.length > limite).length;
      if (longues) liste.push(`${longues} accroche${longues > 1 ? 's dépassent' : ' dépasse'} ${limite} caractères`);
      // ⚠️ Google refuse deux accroches identiques sur une même campagne : le dire ici plutôt
      // que de laisser l'écriture échouer sur un message que personne ne relie au geste.
      const doublons = nouvelles.filter((t, i) => nouvelles.findIndex(
        autre => autre.toLowerCase() === t.toLowerCase()
      ) !== i).length;
      if (doublons) liste.push('des accroches rédigées en double');
      const dejaAuCatalogue = nouvelles.filter(t => (accroches || []).some(
        a => a.texte.toLowerCase() === t.toLowerCase()
      )).length;
      if (dejaAuCatalogue) {
        liste.push(`${dejaAuCatalogue} accroche${dejaAuCatalogue > 1 ? 's existent' : ' existe'} déjà dans le compte — cochez-la plutôt que de la réécrire`);
      }
      return liste;
    }

    /*
     * Les annonces corrigées avant réactivation passent par la même toise : vider un titre de
     * trop ferait descendre sous le minimum, et Google refuserait **après** la confirmation.
     */
    if (!saisie) {
      const bornes = { titre: 30, description: 90, titresMin: 3, descriptionsMin: 2 };
      const liste = [];
      for (const copie of Object.values(edition)) {
        if (!copie) continue;
        const titres = (copie.headlines || []).map(a => (a.text || '').trim()).filter(Boolean);
        const descriptions = (copie.descriptions || []).map(a => (a.text || '').trim()).filter(Boolean);
        /*
         * Chaque entrée est une phrase complète, et dit **ce qu'on a** autant que ce qu'il faut :
         * « 3 titres au minimum » laisse chercher lequel manque, « il n'en reste que 2 » non.
         */
        if (titres.length < bornes.titresMin) {
          liste.push(`il faut ${bornes.titresMin} titres au minimum, il n'en reste que ${titres.length}`);
        }
        if (descriptions.length < bornes.descriptionsMin) {
          liste.push(`il faut ${bornes.descriptionsMin} descriptions au minimum, il n'en reste que ${descriptions.length}`);
        }
        const titresLongs = titres.filter(t => t.length > bornes.titre).length;
        if (titresLongs) liste.push(`${titresLongs} titre${titresLongs > 1 ? 's dépassent' : ' dépasse'} ${bornes.titre} caractères`);
        const descriptionsLongues = descriptions.filter(d => d.length > bornes.description).length;
        if (descriptionsLongues) liste.push(`${descriptionsLongues} description${descriptionsLongues > 1 ? 's dépassent' : ' dépasse'} ${bornes.description} caractères`);
      }
      return [...new Set(liste)];
    }

    if (!brouillon) return [];
    const bornes = contexteBrouillon?.limites || {};
    const titres = (brouillon.headlines || []).map(t => t.trim()).filter(Boolean);
    const descriptions = (brouillon.descriptions || []).map(t => t.trim()).filter(Boolean);
    const liste = [];
    if (!String(brouillon.finalUrl || '').trim()) liste.push('une URL de destination');
    if (titres.length < (bornes.titresMin || 3)) liste.push(`${bornes.titresMin || 3} titres au minimum`);
    if (descriptions.length < (bornes.descriptionsMin || 2)) liste.push(`${bornes.descriptionsMin || 2} descriptions au minimum`);
    if (titres.some(t => t.length > (bornes.titre || 30))) liste.push(`des titres de ${bornes.titre || 30} caractères au plus`);
    if (descriptions.some(d => d.length > (bornes.description || 90))) liste.push(`des descriptions de ${bornes.description || 90} caractères au plus`);
    return liste;
  })();

  /**
   * Ce qui va changer, nommé — un libellé par objet que l'écriture touchera.
   *
   * ⚠️ **Il faut nommer, pas compter.** « 1 annonce sera modifiée » ne dit ni laquelle, ni dans
   * quel groupe, ni si son texte a été corrigé au passage : on confirme une écriture sans
   * savoir ce qu'elle porte. Le décompte seul laissait même croire que le geste avait déjà eu
   * lieu — la question posée était « l'annonce a-t-elle été réactivée ? ».
   *
   * Les libellés viennent de l'état de l'écran, le seul endroit où l'on sache *quelle*
   * occurrence a été retenue. Ils sont ensuite recoupés avec ce que le serveur retient
   * réellement après relecture : une cible écartée entre-temps ne doit pas figurer ici.
   */
  const recapitulatif = () => {
    const lignes = [];

    if (groupes) {
      groupes.forEach((groupe, index) => {
        if (selection[index]) lignes.push({ cible: groupe.cible, titre: groupe.titre, lieu: groupe.lieu });
      });
      return lignes;
    }

    actionnables.forEach((element, index) => {
      if (!selection[index]) return;
      const cible = choixRequis ? candidatesDe(element)[choix[index]] : element.cible;
      if (!cible) return;

      const retouche = edition[index];
      const original = choixRequis ? candidatesDe(element)[choix[index]] : null;
      // Le texte a-t-il été corrigé ? Comparé à ce qui a été lu, pas au constat.
      const modifie = Boolean(retouche && original && (retouche.headlines || []).some(
        (actif, rang) => actif.text !== original.headlines?.[rang]?.text
      ));

      const existantes = (accroches || []).filter(a => accrochesRetenues[a.resourceName]).length;
      const nouvelles = saisie === 'sitelinks'
        ? sitelinksValides().length
        : nouvellesAccroches.map(t => t.trim()).filter(Boolean).length;

      lignes.push({
        /*
         * ⚠️ **La cible, pas une clé recomposée.** Le serveur construit la sienne avec
         * `cleCible`, qui ne suit pas toujours `champsCible` — pour l'isolement, la clé vaut
         * `groupe~critère` quand les champs exigés sont trois. Deviner la formule produisait un
         * récapitulatif vide, et l'écran taisait ce qu'il allait écrire au moment précis où on
         * le lui demandait. On compare donc ce que les deux côtés ont réellement en main.
         */
        cible: { ...element.cible, ...cible },
        titre: choixRequis
          ? (cible.libelle || [cible.campaignName, cible.adGroupName].filter(Boolean).join(' › '))
          : element.name,
        lieu: choixRequis ? element.name : null,
        // Ce qui part, et d'où cela vient : reprendre une accroche du compte et en écrire une
        // nouvelle n'ont pas les mêmes conséquences — la seconde entre au catalogue pour de bon.
        mention: saisie === 'url'
          ? (destination?.voulue?.trim() ? 'nouvelle page' : "retour à la page de l'annonce")
          : saisie === 'isolement'
            ? (isolement ? `${isolement.headlines.filter(Boolean).length} titres · groupe « ${isolement.nomGroupe} »` : null)
            : saisie === 'annonce'
          ? (annonce
            ? `${contenuAnnonce.headlines.filter(h => h.text.trim()).length} titres · ${contenuAnnonce.descriptions.filter(d => d.text.trim()).length} descriptions`
            : null)
          : (saisie === 'accroches' || saisie === 'sitelinks')
          ? [existantes ? `${existantes} reprise${existantes > 1 ? 's' : ''} du compte` : null,
            nouvelles ? `${nouvelles} nouvelle${nouvelles > 1 ? 's' : ''}` : null]
            .filter(Boolean).join(' · ')
          : (modifie ? 'texte corrigé' : null)
      });
    });
    return lignes;
  };

  /** « 4 négatifs », « 3 mots-clés » — l'unité vient du serveur, jamais devinée ici. */
  const compter = (n, unite) => unite
    ? `${n} ${n > 1 ? unite.pluriel : unite.singulier}`
    : `${n} élément${n > 1 ? 's' : ''}`;

  return ReactDOM.createPortal(
    <div
      className="kcorr-fond"
      onClick={() => { if (!occupe) onFermer(); }}
      role="presentation"
    >
      <div
        className="kcorr-boite"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={`${action.libelle} — ${reco.label}`}
      >
        <header className="kcorr-tete">
          <div>
            <h2>{action.libelle}</h2>
            <p className="kcorr-sous-titre">{reco.compte?.name || reco.accountId} · {reco.label}</p>
          </div>
          <button className="kcorr-fermer" onClick={onFermer} disabled={occupe} aria-label="Fermer">
            <X size={18} />
          </button>
        </header>

        <div className="kcorr-corps">
          <p className="kcorr-quoi">{action.description}</p>

          {/* L'attente, quand le choix est sauté : on nomme ce qu'on lit, plutôt que de
              montrer une liste qui va disparaître. */}
          {etape === 'selection' && sautImmediat && (
            <p className="kcorr-chargement">
              <Loader size={14} className="kcorr-tourne" />
              {ATTENTE[saisie] || 'Préparation du brouillon…'}
            </p>
          )}

          {/* ─── Sélection ─────────────────────────────────────────────── */}
          {etape === 'selection' && !sautImmediat && (
            <>
              {actionnables.length === 0 && !action.surLeConstat ? (
                <p className="kcorr-avis kcorr-avis-alerte">
                  <AlertTriangle size={15} />
                  Ce constat ne porte pas les identifiants nécessaires pour agir. Relancez le
                  relevé du compte : les constats enregistrés avant cette version ne les ont pas.
                </p>
              ) : (
                <>
                  {/*
                    * ⚠️ **Pas d'avis quand on a cliqué une ligne précise.** « 1 des 11 éléments
                    * peuvent être corrigés ici » se lit comme une limite de l'outil, alors que
                    * l'utilisateur vient délibérément d'en désigner un seul. L'avis n'a de sens
                    * que sur le geste ouvert depuis la carte, qui prétend couvrir le constat.
                    */}
                  {!elementUnique && actionnables.length < (reco.affectedCount || 0) && (
                    <p className="kcorr-avis">
                      <Info size={15} />
                      {actionnables.length} des {reco.affectedCount} éléments du constat peuvent
                      être corrigés ici{reco.affectedComplet === false
                        ? " — la liste enregistrée est un échantillon, l'outil dédié donne l'ensemble."
                        : '.'}
                      {/* Avec des groupes, l'échantillon a une conséquence propre : un objet
                          peut bloquer plus de lignes que l'aperçu n'en montre. */}
                      {groupes && reco.affectedComplet === false
                        ? ' Les décomptes ci-dessous ne portent que sur les éléments listés.'
                        : ''}
                    </p>
                  )}

                  {/* Plusieurs lignes peuvent désigner le même objet : le dire avant de
                      cliquer, sinon le compte rendu paraîtra incohérent avec la sélection. */}
                  {nombreCibles > 0 && nombreCibles < nombreChoisi && (
                    <p className="kcorr-avis">
                      <Info size={15} />
                      {compter(nombreCibles, action.uniteAction)} à modifier pour&nbsp;
                      {compter(nombreChoisi, action.uniteElement)} sélectionné{nombreChoisi > 1 ? 's' : ''} :
                      un même {action.uniteAction?.singulier || 'élément'} en concerne plusieurs.
                    </p>
                  )}

                  {/* Un bloc par objet modifié : on coche le négatif, pas les mots-clés
                      qu'il bloque — ceux-ci se lisent dessous, pour juger de l'effet. */}
                  {groupes && (
                    <ul className="kcorr-liste">
                      {groupes.map((groupe, index) => (
                        <li key={groupe.cle}>
                          <label className="kcorr-ligne">
                            <input
                              type="checkbox"
                              checked={Boolean(selection[index])}
                              onChange={() => setSelection(p => ({ ...p, [index]: !p[index] }))}
                            />
                            <span className="kcorr-nom">
                              <strong>{groupe.titre}</strong>
                              <span className="kcorr-lieu"> — {groupe.lieu}</span>
                            </span>
                          </label>
                          <div className="kcorr-couvre">
                            Débloque {compter(groupe.elements.length, action.uniteElement)} :
                            <ul>
                              {groupe.elements.slice(0, 6).map((nom, rang) => (
                                <li key={rang}><TexteAccentue texte={nom} /></li>
                              ))}
                              {groupe.elements.length > 6 && (
                                <li className="kcorr-reste">
                                  et {groupe.elements.length - 6} autre
                                  {groupe.elements.length - 6 > 1 ? 's' : ''}
                                </li>
                              )}
                            </ul>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}

                  {manques.length > 0 && (
                    <p className="kcorr-avis kcorr-avis-alerte">
                      <AlertTriangle size={15} />
                      À corriger avant de continuer : {manques.join(' ; ')}.
                    </p>
                  )}

                  {!groupes && (
                  <ul className="kcorr-liste">
                    {actionnables.map((element, index) => (
                      <li key={index}>
                        <label className="kcorr-ligne">
                          {/* ⚠️ Un seul élément à la fois quand il faut rédiger : vingt annonces
                              ne se relisent pas d'un clic, et publier sans relire est hors de
                              question. D'où le bouton radio, qui dit la contrainte sans phrase. */}
                          <input
                            type={saisie ? 'radio' : 'checkbox'}
                            name={saisie ? 'kcorr-element' : undefined}
                            checked={Boolean(selection[index])}
                            disabled={choixRequis && choix[index] === undefined}
                            onChange={() => setSelection(p => (saisie
                              ? { [index]: true }
                              : { ...p, [index]: !p[index] }))}
                          />
                          <span className="kcorr-nom"><TexteAccentue texte={element.name} /></span>
                          {element.value && <span className="kcorr-lieu"> — {element.value}</span>}
                        </label>

                        {/* Un choix à faire : laquelle des occurrences, ou des annonces ? */}
                        {choixRequis && (
                          <div className="kcorr-choix">
                            {choixDetaille && !choixCharge && (
                              <p className="kcorr-chargement">
                                <Loader size={13} className="kcorr-tourne" />
                                Lecture des annonces dans le compte…
                              </p>
                            )}
                            {candidatesDe(element).map((occurrence, rang) => {
                              const retenue = choix[index] === rang;
                              const corrige = retenue ? edition[index] : null;
                              return (
                                <div
                                  key={rang}
                                  className={`kcorr-candidate ${retenue ? 'kcorr-candidate-retenue' : ''}`}
                                >
                                  <label className="kcorr-choix-ligne">
                                    <input
                                      type="radio"
                                      name={`choix-${index}`}
                                      checked={retenue}
                                      onChange={() => retenirAnnonce(index, rang)}
                                    />
                                    <span className="kcorr-candidate-nom">
                                      {/* Une occurrence se nomme elle-même quand elle le peut.
                                          Le chemin campagne › groupe reste le repli, et c'est ce
                                          que porte une occurrence de doublon. */}
                                      {occurrence.libelle || [occurrence.campaignName, occurrence.adGroupName]
                                        .filter(Boolean).join(' › ')}
                                    </span>
                                    {/* ⚠️ Dit avant le choix, pas seulement à l'aperçu : réactiver
                                        une annonce refusée l'écrirait sans qu'elle diffuse. */}
                                    {occurrence.refusee && (
                                      <span className="kcorr-etiquette kcorr-etiquette-refus">refusée par Google</span>
                                    )}
                                    {occurrence.restreinte && (
                                      <span className="kcorr-etiquette">diffusion restreinte</span>
                                    )}
                                  </label>

                                  {/*
                                    * Le contenu de **toutes** les annonces, lu à l'instant.
                                    *
                                    * ⚠️ **Il faut pouvoir comparer sans cliquer.** Ne déplier que
                                    * l'annonce retenue obligeait à essayer chaque bouton radio
                                    * pour lire le texte, puis à revenir : quatre allers-retours
                                    * pour une décision qui tient en un coup d'œil. Celle qu'on
                                    * retient passe en champs modifiables ; les autres se lisent
                                    * en condensé, ce qui est exactement la forme d'une
                                    * comparaison.
                                    */}
                                  {occurrence.headlines && occurrence.headlines.length > 0 && (
                                    retenue ? (
                                      <div className="kcorr-annonce">
                                        {occurrence.finalUrl && (
                                          <p className="kcorr-annonce-url">{occurrence.finalUrl}</p>
                                        )}
                                        {!occurrence.modifiable && (
                                          <div className="kcorr-avis kcorr-avis-geste">
                                            <Info size={14} />
                                            <div>
                                              <p>
                                                {occurrence.type === 'RESPONSIVE_SEARCH_AD'
                                                  ? "Le contenu de cette annonce n'a pas pu être lu entièrement : elle sera réactivée telle quelle."
                                                  : `Format « ${nommerFormat(occurrence.type)} » : Google n'en autorise plus la modification depuis juin 2022. Elle sera réactivée telle quelle.`}
                                              </p>
                                              {/* ⚠️ Le geste qui *marche* quand la modification est
                                                  interdite : reprendre le texte dans une annonce
                                                  responsive, le seul format que Google accepte
                                                  encore. Sans ce bouton, il faudrait recopier à la
                                                  main un texte qu'on a sous les yeux. */}
                                              {occurrence.type !== 'RESPONSIVE_SEARCH_AD'
                                                && (reco.actions || []).some(a => a.saisie === 'rsa') && (
                                                <button
                                                  type="button"
                                                  className="kcorr-ajouter"
                                                  onClick={() => reprendreDansUneNouvelle(index, occurrence)}
                                                >
                                                  Reprendre ce texte pour en écrire une nouvelle
                                                </button>
                                              )}
                                            </div>
                                          </div>
                                        )}
                                        {[
                                          { champ: 'headlines', titre: 'Titres', limite: 30 },
                                          { champ: 'descriptions', titre: 'Descriptions', limite: 90 }
                                        ].map(({ champ, titre, limite }) => (
                                          <div className="kcorr-bloc-textes" key={champ}>
                                            <h3>{titre}</h3>
                                            <ul className="kcorr-textes">
                                              {((corrige || occurrence)[champ] || []).map((actif, position) => (
                                                <li key={position}>
                                                  {corrige ? (
                                                    <input
                                                      type="text"
                                                      value={actif.text}
                                                      onChange={(e) => changerMessage(index, champ, position, e.target.value)}
                                                    />
                                                  ) : (
                                                    <span className="kcorr-fige">{actif.text}</span>
                                                  )}
                                                  {/* L'épinglage se lit, et se conserve : il ne
                                                      s'écrit pas ici, mais le taire laisserait
                                                      croire qu'il a disparu à la réécriture. */}
                                                  {actif.pinnedField && (
                                                    <span className="kcorr-epingle">{actif.pinnedField.replace(/_/g, ' ').toLowerCase()}</span>
                                                  )}
                                                  <span className={`kcorr-compte ${actif.text.length > limite ? 'kcorr-compte-trop' : ''}`}>
                                                    {actif.text.length}/{limite}
                                                  </span>
                                                </li>
                                              ))}
                                            </ul>
                                          </div>
                                        ))}
                                      </div>
                                    ) : (
                                      /*
                                       * En condensé : les messages séparés par des points
                                       * médians, dans l'ordre où Google les a enregistrés.
                                       * Deux lignes suffisent à voir qu'une annonce parle du
                                       * produit et l'autre de la marque — c'est la question
                                       * qu'on se pose en choisissant.
                                       */
                                      <div className="kcorr-apercu-annonce">
                                        <p className="kcorr-apercu-titres">
                                          {occurrence.headlines.map(a => a.text).join(' · ')}
                                        </p>
                                        {occurrence.descriptions?.length > 0 && (
                                          <p className="kcorr-apercu-descriptions">
                                            {occurrence.descriptions.map(d => d.text).join(' · ')}
                                          </p>
                                        )}
                                        {occurrence.finalUrl && (
                                          <p className="kcorr-annonce-url">{occurrence.finalUrl}</p>
                                        )}
                                      </div>
                                    )
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                  )}
                </>
              )}
            </>
          )}

          {/* ─── Catégorie Google des produits d'un flux ────────────────── */}
          {etape === 'categorie' && flux && (() => {
            const retenus = produitsRetenus();
            return (
              <>
                <p className="kcorr-avis">
                  <Info size={15} />
                  {flux.restants} produit{flux.restants > 1 ? 's' : ''} sans catégorie
                  {flux.dejaCorriges > 0 && ` (${flux.dejaCorriges} déjà corrigé${flux.dejaCorriges > 1 ? 's' : ''} ici)`}.
                  {' '}Le flux source n'est pas modifié : la valeur sort dans le flux supplémentaire.
                </p>

                {/* ⚠️ Un flux sans taxonomie propre ne donne rien pour distinguer ses produits :
                    le dire vaut mieux que de laisser croire à un défaut de l'écran. */}
                {flux.sansTaxonomie && (
                  <p className="kcorr-avis kcorr-avis-alerte">
                    <AlertTriangle size={15} />
                    Ce flux ne porte aucune catégorie de marchand : ses produits ne peuvent pas
                    être distingués ici. Une seule catégorie s'appliquera à tous ceux que vous
                    cochez — choisissez-la assez large.
                  </p>
                )}

                <div className="kcorr-bloc-textes">
                  <h3>
                    Catégorie à affecter
                    {retenus.total > 0 && <span className="kcorr-compte">{retenus.total} produits</span>}
                  </h3>
                  <ChoixCategorieGoogle
                    token={token}
                    valeur={flux.categorie?.id || null}
                    nomValeur={flux.categorie?.name || null}
                    onChoisir={(categorie) => setFlux(f => ({ ...f, categorie }))}
                    onEffacer={() => setFlux(f => ({ ...f, categorie: null }))}
                  />
                </div>

                <div className="kcorr-bloc-textes">
                  <h3>
                    {flux.sansTaxonomie ? 'Produits concernés' : 'Groupes de votre flux'}
                    <span className="kcorr-compte">{flux.groupes.length}</span>
                  </h3>
                  <ul className="kcorr-liste">
                    {flux.groupes.map((groupe, index) => (
                      <li key={groupe.typeProduit || 'sans-type'}>
                        <label className="kcorr-ligne">
                          <input
                            type="checkbox"
                            checked={Boolean(flux.retenus[index])}
                            onChange={() => setFlux(f => ({
                              ...f, retenus: { ...f.retenus, [index]: !f.retenus[index] }
                            }))}
                          />
                          <span className="kcorr-nom">
                            {groupe.typeProduit || 'Sans catégorie de marchand'}
                            <span className="kcorr-extension-detail">
                              {groupe.produits.slice(0, 2).map(p => p.titre).filter(Boolean).join(' · ')}
                            </span>
                          </span>
                          <span className="kcorr-etiquette">{groupe.total} produit{groupe.total > 1 ? 's' : ''}</span>
                        </label>
                      </li>
                    ))}
                  </ul>
                </div>

                {/*
                  * ⚠️ **L'aperçu d'un groupe est borné à vingt produits**, mais la correction
                  * porte sur le groupe entier. Le taire ferait croire qu'on n'en corrige que
                  * vingt — ou pire, laisserait découvrir l'écart après l'écriture.
                  */}
                {retenus.total > retenus.apercu && (
                  <p className="kcorr-avis">
                    <Info size={15} />
                    {retenus.apercu} produits sont listés ici, mais la correction portera sur les
                    {' '}{retenus.total} du ou des groupes cochés.
                  </p>
                )}

                {manques.length > 0 && (
                  <p className="kcorr-avis kcorr-avis-alerte">
                    <AlertTriangle size={15} />
                    À corriger avant de continuer : {manques.join(' ; ')}.
                  </p>
                )}
              </>
            );
          })()}

          {/* ─── Analyse d'une page de destination (lecture seule) ──────── */}
          {etape === 'analyse' && analyse && (() => {
            const lu = analyse.contenu || {};
            const coh = analyse.coherence;
            const terrain = analyse.vitesse?.mobile?.terrain;
            const labo = analyse.vitesse?.mobile?.laboratoire;
            return (
              <>
                <p className="kcorr-avis">
                  <Info size={15} />
                  Rien n'est modifié ici. Google juge la page sur ce qu'elle apporte à quelqu'un
                  qui vient de cliquer : sa vitesse, et le fait qu'elle traite bien du sujet.
                </p>
                <p className="kcorr-annonce-url">{analyse.url}</p>
                {analyse.destination?.origine === 'annonce' && (
                  <p className="kcorr-lieu">Héritée de l'annonce : ce mot-clé n'a pas de page propre.</p>
                )}

                {/* La cohérence mot-clé ↔ page : la question même que pose le palier. */}
                {coh && (
                  <div className="kcorr-bloc-textes">
                    <h3>Le mot-clé sur la page</h3>
                    <ul className="kcorr-verdicts">
                      {[
                        { quoi: 'Dans le titre', etat: coh.titre, texte: lu.titre },
                        { quoi: 'Dans un titre H1', etat: coh.h1, texte: (lu.h1 || []).join(' · ') }
                      ].map(({ quoi, etat, texte }) => (
                        <li key={quoi} className={etat.entier ? 'kcorr-ok' : 'kcorr-manque'}>
                          {etat.entier ? <Check size={14} /> : <AlertTriangle size={14} />}
                          <span>
                            <strong>{quoi}</strong>
                            {etat.entier
                              ? ' : oui'
                              : etat.mots > 0
                                ? ` : partiellement (${etat.mots} mot${etat.mots > 1 ? 's' : ''} sur ${etat.total})`
                                : ' : non'}
                            {texte ? <span className="kcorr-extension-detail">{texte}</span> : null}
                          </span>
                        </li>
                      ))}
                      <li className={coh.texteSuffisant ? 'kcorr-ok' : 'kcorr-manque'}>
                        {coh.texteSuffisant ? <Check size={14} /> : <AlertTriangle size={14} />}
                        <span>
                          <strong>Volume de texte</strong> : {lu.motsCount ?? '—'} mots
                          {coh.texteSuffisant ? '' : ` — en deçà de ${analyse.limites?.motsMinimum || 150}, une page a peu de chances de convaincre qu'elle traite le sujet`}
                        </span>
                      </li>
                    </ul>
                    {/* ⚠️ Le crawl ne rend pas le JavaScript : le dire plutôt que de conclure. */}
                    {lu.peutEtreMonopage && (
                      <p className="kcorr-avis kcorr-avis-alerte">
                        <AlertTriangle size={15} />
                        Presque aucun texte n'a été lu. Si cette page est une application monopage,
                        son contenu est écrit par JavaScript et nos outils ne le voient pas — Google,
                        lui, le voit. Ce verdict est alors à écarter.
                      </p>
                    )}
                  </div>
                )}

                <div className="kcorr-bloc-textes">
                  <h3>Vitesse <span className="kcorr-compte">mobile</span></h3>
                  {analyse.vitesse?.mobile?.erreur ? (
                    <p className="kcorr-lieu">Mesure indisponible : {analyse.vitesse.mobile.erreur}</p>
                  ) : (
                    <ul className="kcorr-verdicts">
                      <li className={terrain?.verdict === 'SLOW' ? 'kcorr-manque' : 'kcorr-ok'}>
                        {terrain?.verdict === 'SLOW' ? <AlertTriangle size={14} /> : <Check size={14} />}
                        <span>
                          <strong>Visites réelles</strong> : {terrain?.verdict || 'aucune donnée'}
                          <span className="kcorr-extension-detail">{terrain?.precision}</span>
                          {(terrain?.hors_seuil || []).length > 0 && (
                            <span className="kcorr-extension-detail">{terrain.hors_seuil.join(' · ')}</span>
                          )}
                        </span>
                      </li>
                      {labo && (
                        <li className={(labo.note_performance ?? 100) < 50 ? 'kcorr-manque' : 'kcorr-ok'}>
                          {(labo.note_performance ?? 100) < 50 ? <AlertTriangle size={14} /> : <Check size={14} />}
                          <span>
                            <strong>Laboratoire</strong> : {labo.note_performance ?? '—'}/100
                            <span className="kcorr-extension-detail">{labo.precision}</span>
                            {(labo.opportunites || []).slice(0, 3).map(o => (
                              <span className="kcorr-extension-detail" key={o.audit}>
                                {o.libelle}{o.economie_ms ? ` — ${Math.round(o.economie_ms)} ms` : ''}
                              </span>
                            ))}
                          </span>
                        </li>
                      )}
                    </ul>
                  )}
                </div>

                {lu.urlFinale && (
                  <p className="kcorr-avis kcorr-avis-alerte">
                    <AlertTriangle size={15} />
                    L'adresse redirige vers {lu.urlFinale} ({lu.sauts} saut{lu.sauts > 1 ? 's' : ''}).
                    Le délai s'ajoute au temps de chargement mesuré par Google.
                  </p>
                )}
              </>
            );
          })()}

          {/* ─── Changer la page de destination d'un mot-clé ────────────── */}
          {etape === 'url' && destination && (
            <>
              <p className="kcorr-avis">
                <Info size={15} />
                {destination.origine === 'annonce'
                  ? "Ce mot-clé n'a pas de page propre : il reprend celle de l'annonce. En lui en posant une, elle primera — pour lui seul."
                  : destination.origine === 'mot-clé'
                    ? "Ce mot-clé porte déjà sa propre page, qui prime sur celle de l'annonce."
                    : "Ni ce mot-clé ni les annonces actives du groupe ne portent de page de destination."}
              </p>

              {destination.courante && (
                <div className="kcorr-bloc-textes">
                  <h3>Page actuelle <span className="kcorr-compte">{destination.origine}</span></h3>
                  <p className="kcorr-annonce-url">{destination.courante}</p>
                  {/* Plusieurs annonces peuvent pointer ailleurs : le taire ferait croire à une
                      destination unique. */}
                  {destination.heritees?.length > 1 && (
                    <p className="kcorr-lieu">
                      Les annonces du groupe pointent vers {destination.heritees.length} pages différentes.
                    </p>
                  )}
                </div>
              )}

              <label className="kcorr-champ">
                <span>Page de destination de ce mot-clé</span>
                <input
                  type="url"
                  value={destination.voulue}
                  placeholder="https://…"
                  onChange={(e) => setDestination(d => ({ ...d, voulue: e.target.value }))}
                />
              </label>
              <p className="kcorr-lieu">
                Laisser vide retire la page propre du mot-clé : il reprendra celle de l'annonce.
              </p>

              {manques.length > 0 && (
                <p className="kcorr-avis kcorr-avis-alerte">
                  <AlertTriangle size={15} />
                  À corriger avant de continuer : {manques.join(' ; ')}.
                </p>
              )}
            </>
          )}

          {/* ─── Isoler le mot-clé dans un groupe dédié ─────────────────── */}
          {etape === 'isolement' && isolement && (
            <>
              <p className="kcorr-avis">
                <Info size={15} />
                Quatre écritures en une seule requête : le groupe est créé, le mot-clé y est
                recopié, l'annonce y est publiée, et le mot-clé d'origine passe en pause. Google
                applique tout ou rien.
                {isolement.contexte?.enchereAttendue
                  ? ` La campagne est en ${isolement.contexte.strategieEnchere} : l'enchère du groupe d'origine est reprise.`
                  : ` La campagne est en ${isolement.contexte?.strategieEnchere || 'stratégie automatique'} : le nouveau groupe n'a pas d'enchère propre.`}
              </p>

              <label className="kcorr-champ">
                <span>Nom du nouveau groupe</span>
                <input
                  type="text"
                  value={isolement.nomGroupe}
                  onChange={(e) => changerIsolement('nomGroupe', e.target.value)}
                />
              </label>

              <label className="kcorr-champ">
                <span>Page de destination de l'annonce</span>
                <input
                  type="url"
                  value={isolement.finalUrl}
                  placeholder="https://…"
                  onChange={(e) => changerIsolement('finalUrl', e.target.value)}
                />
              </label>

              {[
                { champ: 'headlines', titre: 'Titres', limite: 30, maximum: 15 },
                { champ: 'descriptions', titre: 'Descriptions', limite: 90, maximum: 4 }
              ].map(({ champ, titre, limite, maximum }) => (
                <div className="kcorr-bloc-textes" key={champ}>
                  <h3>{titre} <span className="kcorr-compte">{isolement[champ].length}/{maximum}</span></h3>
                  <ul className="kcorr-textes">
                    {isolement[champ].map((texte, rang) => (
                      <li key={rang}>
                        <input
                          type="text"
                          value={texte}
                          onChange={(e) => changerTitreIsolement(champ, rang, e.target.value)}
                        />
                        <span className={`kcorr-compte ${texte.length > limite ? 'kcorr-compte-trop' : ''}`}>
                          {texte.length}/{limite}
                        </span>
                        <button
                          type="button"
                          className="kcorr-retirer"
                          onClick={() => setIsolement(a => ({ ...a, [champ]: a[champ].filter((_, i) => i !== rang) }))}
                          aria-label="Retirer cette ligne"
                        >
                          <X size={14} />
                        </button>
                      </li>
                    ))}
                  </ul>
                  {isolement[champ].length < maximum && (
                    <button
                      type="button"
                      className="kcorr-ajouter"
                      onClick={() => setIsolement(a => ({ ...a, [champ]: [...a[champ], ''] }))}
                    >
                      + Ajouter
                    </button>
                  )}
                </div>
              ))}

              {manques.length > 0 && (
                <p className="kcorr-avis kcorr-avis-alerte">
                  <AlertTriangle size={15} />
                  À corriger avant de continuer : {manques.join(' ; ')}.
                </p>
              )}
            </>
          )}

          {/* ─── Correction d'une annonce existante ────────────────────── */}
          {etape === 'annonce' && annonce && contenuAnnonce && (
            <>
              {/*
                * ⚠️ **Un groupe peut porter plusieurs annonces**, et c'est le cas voulu : Google
                * conseille d'en avoir au moins deux. Le constat d'un mot-clé n'en désigne aucune
                * — il faut donc choisir laquelle corriger, et le texte de chacune est la seule
                * chose qui permette de trancher.
                */}
              {annonce.fiches.length > 1 && (
                <div className="kcorr-bloc-textes">
                  <h3>
                    Annonce à corriger
                    <span className="kcorr-compte">{annonce.fiches.length} dans ce groupe</span>
                  </h3>
                  <div className="kcorr-choix">
                    {annonce.fiches.map(fiche => (
                      <label key={fiche.adId} className="kcorr-choix-ligne">
                        <input
                          type="radio"
                          name="kcorr-annonce-du-groupe"
                          checked={String(annonce.adId) === String(fiche.adId)}
                          onChange={() => retenirAnnonceDuGroupe(fiche.adId)}
                        />
                        <span>{(fiche.headlines || []).map(h => h.text).slice(0, 3).join(' · ')}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
              <p className="kcorr-avis">
                <Info size={15} />
                Texte lu à l'instant dans le compte. L'annonce est modifiée <strong>sur place</strong> :
                elle garde son identifiant et son historique. Google juge la qualité sur le nombre de
                titres et leur variété — quinze titres et quatre descriptions sont le maximum.
              </p>

              {annonce.fiche.finalUrl && (
                <p className="kcorr-annonce-url">{annonce.fiche.finalUrl}</p>
              )}

              {/* ⚠️ Le mot-clé sous les yeux, et le décompte des titres qui le reprennent : c'est
                  la consigne de Google pour la pertinence d'annonce, et c'est ce qu'on vient
                  corriger. Le taire laisserait rédiger à côté de la question posée. */}
              {annonce.element.cible?.motCle && (() => {
                const mot = annonce.element.cible.motCle.toLowerCase();
                const repris = contenuAnnonce.headlines.filter(h => (h.text || '').toLowerCase().includes(mot)).length;
                // ⚠️ L'alerte s'éteint dès que la consigne est tenue : un avertissement orange
                // sur une condition satisfaite devient du bruit, et on cesse de le lire.
                return (
                  <p className={`kcorr-avis ${repris > 0 ? '' : 'kcorr-avis-alerte'}`}>
                    {repris > 0 ? <Check size={15} /> : <AlertTriangle size={15} />}
                    Mot-clé à reprendre : <strong>{annonce.element.cible.motCle}</strong> —{' '}
                    {repris > 0
                      ? `${repris} titre${repris > 1 ? 's le reprennent' : ' le reprend'}.`
                      : "aucun titre ne le reprend aujourd'hui."}
                  </p>
                );
              })()}

              {[
                { champ: 'headlines', titre: 'Titres', limite: 30, maximum: 15 },
                { champ: 'descriptions', titre: 'Descriptions', limite: 90, maximum: 4 }
              ].map(({ champ, titre, limite, maximum }) => (
                <div className="kcorr-bloc-textes" key={champ}>
                  <h3>
                    {titre}
                    <span className="kcorr-compte">{contenuAnnonce[champ].length}/{maximum}</span>
                  </h3>
                  <ul className="kcorr-textes">
                    {contenuAnnonce[champ].map((actif, rang) => (
                      <li key={rang}>
                        <input
                          type="text"
                          value={actif.text}
                          onChange={(e) => changerMessageAnnonce(champ, rang, e.target.value)}
                        />
                        {/* L'épinglage se conserve à la réécriture : le taire laisserait croire
                            qu'il a disparu. */}
                        {actif.pinnedField && (
                          <span className="kcorr-epingle">{actif.pinnedField.replace(/_/g, ' ').toLowerCase()}</span>
                        )}
                        <span className={`kcorr-compte ${actif.text.length > limite ? 'kcorr-compte-trop' : ''}`}>
                          {actif.text.length}/{limite}
                        </span>
                        <button
                          type="button"
                          className="kcorr-retirer"
                          onClick={() => retirerMessageAnnonce(champ, rang)}
                          aria-label="Retirer cette ligne"
                        >
                          <X size={14} />
                        </button>
                      </li>
                    ))}
                  </ul>
                  {contenuAnnonce[champ].length < maximum && (
                    <button type="button" className="kcorr-ajouter" onClick={() => ajouterMessageAnnonce(champ)}>
                      + Ajouter {champ === 'headlines' ? 'un titre' : 'une description'}
                    </button>
                  )}
                </div>
              ))}

              {manques.length > 0 && (
                <p className="kcorr-avis kcorr-avis-alerte">
                  <AlertTriangle size={15} />
                  À corriger avant de continuer : {manques.join(' ; ')}.
                </p>
              )}
            </>
          )}

          {/* ─── Accroches : choisir dans le compte, ou rédiger ─────────── */}
          {etape === 'accroches' && (() => {
            // Le vocabulaire change, l'écran non : les deux familles d'extension se choisissent
            // et se rédigent de la même façon.
            const estLien = saisie === 'sitelinks';
            const nom = estLien
              ? { un: 'lien annexe', des: 'liens annexes', ecrit: 'écrit', pluriel: 's' }
              : { un: 'accroche', des: 'accroches', ecrit: 'écrite', pluriel: 's' };
            const recommande = contexteBrouillon?.limites?.recommande || 4;
            return (
            <>
              <p className="kcorr-avis">
                <Info size={15} />
                {accroches?.length
                  ? `${accroches.length} ${accroches.length > 1 ? nom.des : nom.un} ${accroches.length > 1 ? 'ont' : 'a'} déjà été ${nom.ecrit}${accroches.length > 1 ? nom.pluriel : ''} pour d'autres campagnes de ce compte : cochez ${estLien ? 'ceux' : 'celles'} qui conviennent, et rédigez ce qui manque. Google en recommande ${recommande} par campagne.`
                  : `Aucun${estLien ? '' : 'e'} ${nom.un} n'a encore été ${nom.ecrit} dans ce compte. Rédigez-${estLien ? 'les' : 'les'} ici — Google en recommande ${recommande} par campagne.`}
              </p>

              {accroches?.length > 0 && (
                <div className="kcorr-bloc-textes">
                  {/*
                    * ⚠️ **« du compte » se lisait comme « au niveau du compte »**, qui est un
                    * rattachement précis chez Google — celui dont *toutes* les campagnes
                    * héritent. Ce n'est pas ce qu'on liste : ce sont les accroches **déjà
                    * écrites** dans le compte, rattachées campagne par campagne. Un compte en a
                    * eu 23 affichées sous ce titre alors qu'aucune n'était au niveau compte.
                    */}
                  <h3>
                    {estLien ? 'Liens annexes déjà écrits' : 'Accroches déjà écrites'}
                    <span className="kcorr-compte">réutilisables</span>
                  </h3>
                  <ul className="kcorr-liste">
                    {accroches.map(accroche => (
                      <li key={accroche.resourceName}>
                        <label className="kcorr-ligne">
                          <input
                            type="checkbox"
                            checked={Boolean(accrochesRetenues[accroche.resourceName])}
                            /* ⚠️ Une accroche déjà posée sur cette campagne ne se repropose
                               pas : Google refuserait le doublon, et l'échec serait
                               incompréhensible. */
                            disabled={accroche.dejaPosee}
                            onChange={() => setAccrochesRetenues(p => ({
                              ...p, [accroche.resourceName]: !p[accroche.resourceName]
                            }))}
                          />
                          <span className="kcorr-nom">
                            {accroche.texte}
                            {/* Un lien annexe se juge sur sa destination et ses descriptions,
                                pas sur son seul libellé : deux « En savoir plus » peuvent
                                pointer vers deux pages différentes. */}
                            {estLien && (accroche.description1 || accroche.url) && (
                              <span className="kcorr-extension-detail">
                                {[accroche.description1, accroche.description2].filter(Boolean).join(' · ')}
                                {/* ⚠️ Un `<span>`, pas un `<p>` : l'ensemble vit déjà dans un
                                    `<span>`, et un paragraphe imbriqué est reparenté par le
                                    navigateur — l'URL se collait au texte qui précède. */}
                                {accroche.url && <span className="kcorr-extension-url">{accroche.url}</span>}
                              </span>
                            )}
                          </span>
                          {accroche.dejaPosee ? (
                            <span className="kcorr-etiquette">déjà sur cette campagne</span>
                          ) : (
                            /* Le nombre de campagnes qui l'emploient dit si c'est la ligne
                               éditoriale du compte ou un cas particulier — et « inutilisée »
                               se dit, plutôt que de laisser une ligne nue qu'on lirait comme
                               une donnée manquante. */
                            <span className="kcorr-etiquette">
                              {accroche.campagnes > 0
                                ? `sur ${accroche.campagnes} campagne${accroche.campagnes > 1 ? 's' : ''}`
                                : 'inutilisée'}
                            </span>
                          )}
                        </label>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="kcorr-bloc-textes">
                <h3>{estLien ? 'Nouveaux liens annexes' : 'Nouvelles accroches'}</h3>

                {estLien && (
                  <ul className="kcorr-sitelinks">
                    {nouveauxSitelinks.map((lien, rang) => (
                      <li key={rang}>
                        <div className="kcorr-sitelink-tete">
                          <input
                            type="text"
                            value={lien.texte}
                            placeholder="Nos tarifs"
                            onChange={(e) => changerSitelink(rang, 'texte', e.target.value)}
                          />
                          <span className={`kcorr-compte ${lien.texte.length > (contexteBrouillon?.limites?.texte || 25) ? 'kcorr-compte-trop' : ''}`}>
                            {lien.texte.length}/{contexteBrouillon?.limites?.texte || 25}
                          </span>
                          {nouveauxSitelinks.length > 1 && (
                            <button
                              type="button"
                              className="kcorr-retirer"
                              onClick={() => setNouveauxSitelinks(l => l.filter((_, i) => i !== rang))}
                              aria-label="Retirer ce lien"
                            >
                              <X size={14} />
                            </button>
                          )}
                        </div>
                        <input
                          className="kcorr-sitelink-url"
                          type="url"
                          value={lien.url}
                          placeholder="https://… (obligatoire)"
                          onChange={(e) => changerSitelink(rang, 'url', e.target.value)}
                        />
                        {/* Les deux lignes de description sont facultatives, mais la seconde
                            suppose la première : Google refuse l'inverse. */}
                        {['description1', 'description2'].map((champ, ligne) => (
                          <div className="kcorr-sitelink-description" key={champ}>
                            <input
                              type="text"
                              value={lien[champ]}
                              placeholder={`Description ${ligne + 1} (facultative)`}
                              onChange={(e) => changerSitelink(rang, champ, e.target.value)}
                            />
                            <span className={`kcorr-compte ${lien[champ].length > (contexteBrouillon?.limites?.description || 35) ? 'kcorr-compte-trop' : ''}`}>
                              {lien[champ].length}/{contexteBrouillon?.limites?.description || 35}
                            </span>
                          </div>
                        ))}
                      </li>
                    ))}
                  </ul>
                )}

                {!estLien && (
                <ul className="kcorr-textes">
                  {nouvellesAccroches.map((texte, rang) => (
                    <li key={rang}>
                      <input
                        type="text"
                        value={texte}
                        placeholder="Livraison offerte"
                        onChange={(e) => changerNouvelleAccroche(rang, e.target.value)}
                      />
                      <span className={`kcorr-compte ${texte.length > (contexteBrouillon?.limites?.texte || 25) ? 'kcorr-compte-trop' : ''}`}>
                        {texte.length}/{contexteBrouillon?.limites?.texte || 25}
                      </span>
                      {nouvellesAccroches.length > 1 && (
                        <button
                          type="button"
                          className="kcorr-retirer"
                          onClick={() => setNouvellesAccroches(l => l.filter((_, i) => i !== rang))}
                          aria-label="Retirer cette ligne"
                        >
                          <X size={14} />
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
                )}
                <button
                  type="button"
                  className="kcorr-ajouter"
                  onClick={() => (estLien
                    ? setNouveauxSitelinks(l => [...l, { texte: '', description1: '', description2: '', url: '' }])
                    : setNouvellesAccroches(l => [...l, '']))}
                >
                  + Ajouter {estLien ? 'un lien annexe' : 'une accroche'}
                </button>
              </div>

              {manques.length > 0 && (
                <p className="kcorr-avis kcorr-avis-alerte">
                  <AlertTriangle size={15} />
                  À corriger avant de continuer : {manques.join(' ; ')}.
                </p>
              )}
            </>
            );
          })()}

          {/* ─── Rédaction ─────────────────────────────────────────────── */}
          {etape === 'redaction' && brouillon && (
            <>
              <p className="kcorr-avis">
                <Info size={15} />
                {brouillon.redigePar === 'reprise'
                  ? `Texte repris de « ${contexteBrouillon?.reprisDe} », au format ${nommerFormat(contexteBrouillon?.formatRepris)}. Corrigez-le : il partira dans une annonce responsive, et l'ancienne restera en pause.`
                  : brouillon.redigePar === 'ia'
                  ? "Brouillon rédigé à partir des mots-clés du groupe et de ses annonces existantes."
                  : brouillon.redigePar === 'mixte'
                    ? "Brouillon rédigé à partir du groupe, complété par un gabarit là où il manquait des lignes."
                    : "Gabarit tiré des mots-clés du groupe : il évite la page blanche, il ne tient pas lieu de rédaction."}
                {/* Le décompte des mots-clés éclaire un brouillon rédigé *à partir d'eux*. Sur
                    un texte repris d'une annonce existante, il ne dit rien du contenu proposé —
                    et « aucun mot-clé actif » se lirait comme un reproche hors sujet. */}
                {brouillon.redigePar === 'reprise' ? '' : (contexteBrouillon?.nombreMotsCles
                  ? ` ${contexteBrouillon.nombreMotsCles} mot${contexteBrouillon.nombreMotsCles > 1 ? 's' : ''}-clé${contexteBrouillon.nombreMotsCles > 1 ? 's' : ''} actif${contexteBrouillon.nombreMotsCles > 1 ? 's' : ''} dans ce groupe.`
                  : ' Ce groupe n\'a aucun mot-clé actif.')}
              </p>

              {/* ⚠️ Pas de valeur par défaut raisonnable pour une page de destination : une
                  annonce qui pointe ailleurs dépense au lieu de ne rien faire. */}
              {!contexteBrouillon?.urlDeduite && (
                <p className="kcorr-avis kcorr-avis-alerte">
                  <AlertTriangle size={15} />
                  Aucune URL de destination n'a pu être lue dans ce groupe : indiquez-la.
                </p>
              )}

              <label className="kcorr-champ">
                <span>Page de destination</span>
                <input
                  type="url"
                  value={brouillon.finalUrl || ''}
                  placeholder="https://..."
                  onChange={(e) => setBrouillon(a => ({ ...a, finalUrl: e.target.value }))}
                />
              </label>

              <div className="kcorr-chemins">
                {['path1', 'path2'].map((champ, rang) => (
                  <label className="kcorr-champ" key={champ}>
                    <span>Chemin affiché {rang + 1}</span>
                    <input
                      type="text"
                      value={brouillon[champ] || ''}
                      maxLength={contexteBrouillon?.limites?.chemin || 15}
                      placeholder="facultatif"
                      onChange={(e) => setBrouillon(a => ({ ...a, [champ]: e.target.value }))}
                    />
                  </label>
                ))}
              </div>

              {[
                { champ: 'headlines', titre: 'Titres', limite: contexteBrouillon?.limites?.titre || 30, maximum: contexteBrouillon?.limites?.titresMax || 15 },
                { champ: 'descriptions', titre: 'Descriptions', limite: contexteBrouillon?.limites?.description || 90, maximum: contexteBrouillon?.limites?.descriptionsMax || 4 }
              ].map(({ champ, titre, limite, maximum }) => (
                <div className="kcorr-bloc-textes" key={champ}>
                  <h3>{titre} <span className="kcorr-compte">{(brouillon[champ] || []).length}/{maximum}</span></h3>
                  <ul className="kcorr-textes">
                    {(brouillon[champ] || []).map((texte, rang) => (
                      <li key={rang}>
                        <input
                          type="text"
                          value={texte}
                          onChange={(e) => changerTexte(champ, rang, e.target.value)}
                        />
                        {/* Le décompte se lit pendant la frappe : Google refuse après coup, et
                            un refus après confirmation arrive au pire moment. */}
                        <span className={`kcorr-compte ${texte.length > limite ? 'kcorr-compte-trop' : ''}`}>
                          {texte.length}/{limite}
                        </span>
                        <button
                          type="button"
                          className="kcorr-retirer"
                          onClick={() => retirerTexte(champ, rang)}
                          aria-label="Retirer cette ligne"
                        >
                          <X size={14} />
                        </button>
                      </li>
                    ))}
                  </ul>
                  {(brouillon[champ] || []).length < maximum && (
                    <button type="button" className="kcorr-ajouter" onClick={() => ajouterTexte(champ)}>
                      + Ajouter
                    </button>
                  )}
                </div>
              ))}

              {manques.length > 0 && (
                <p className="kcorr-avis kcorr-avis-alerte">
                  <AlertTriangle size={15} />
                  Il manque {manques.join(', ')}.
                </p>
              )}
            </>
          )}

          {/* ─── Aperçu ────────────────────────────────────────────────── */}
          {etape === 'apercu' && apercu && (
            <>
              {/* Ce que l'écriture dépasse : un négatif de liste partagée agit sur des
                  campagnes que le constat ne montre pas. À lire avant de confirmer. */}
              {(apercu.avertissements || []).map((texte, i) => (
                <p key={i} className="kcorr-avis kcorr-avis-alerte">
                  <ShieldAlert size={15} />
                  {texte}
                </p>
              ))}

              {/*
                * ⚠️ **Dire que rien n'est écrit, avant de dire ce qui le sera.**
                *
                * Cet écran annonçait « 1 annonce sera modifié » au futur, sous un bouton
                * « Corriger dans Google Ads » : on ne savait plus si le geste avait déjà eu
                * lieu. Un aperçu doit d'abord se présenter comme tel.
                */}
              <p className="kcorr-avis kcorr-avis-ok">
                <Info size={15} />
                Rien n'a encore été écrit dans Google Ads. Voici ce que fera la confirmation.
              </p>

              <p className="kcorr-verdict">
                {/* Un verbe, jamais un participe : il ne s'accorde avec rien, et il nomme le
                    geste — « modifier » était faux pour une création. */}
                Vous allez {action.verbe || 'modifier'}{' '}
                <strong>{compter(apercu.aCorriger.length, action.uniteAction)}</strong>
                {/* Ce que cela règle dans le constat, quand ce n'est pas un pour un. */}
                {apercu.elementsCouverts > apercu.aCorriger.length && action.uniteElement
                  ? `, ce qui débloque ${compter(apercu.elementsCouverts, action.uniteElement)}`
                  : ''}.
              </p>

              {/* Nommer chaque objet touché. Les libellés sont recoupés avec ce que le serveur
                  retient après relecture : une cible écartée depuis ne doit pas figurer ici. */}
              {(() => {
                // Deux cibles sont la même si elles s'accordent sur tous les champs exigés par
                // l'action — la seule identité que les deux côtés partagent avec certitude.
                const memeCible = (a, b) => (action.champsCible || []).every(
                  champ => String(a?.[champ] ?? '') === String(b?.[champ] ?? '')
                );
                const retenues = apercu.aCorriger.map(c => c.cible).filter(Boolean);
                const lignes = recapitulatif().filter(l => retenues.some(r => memeCible(l.cible, r)));
                // Aucune correspondance : on préfère ne rien lister plutôt que lister à côté.
                if (lignes.length === 0) return null;
                return (
                  <ul className="kcorr-recap">
                    {lignes.map((ligne, i) => (
                      <li key={i}>
                        <span className="kcorr-recap-nom"><TexteAccentue texte={ligne.titre} /></span>
                        {ligne.lieu && <span className="kcorr-lieu"> — {ligne.lieu}</span>}
                        {ligne.mention && <span className="kcorr-etiquette">{ligne.mention}</span>}
                      </li>
                    ))}
                  </ul>
                );
              })()}

              {/*
                * ⚠️ **« Déjà réglé » et « impossible » ne se mélangent pas.** Les premières sont
                * une bonne nouvelle — quelqu'un s'en est chargé —, les secondes disent qu'un
                * geste n'aurait pas l'effet annoncé : réactiver une annonce refusée par Google
                * l'écrirait `ENABLED` sans qu'elle diffuse, et le constat reviendrait au relevé
                * suivant. Les afficher sous le même ✓ ferait passer un empêchement pour un
                * travail accompli.
                */}
              {(() => {
                const reglees = apercu.ecartees.filter(e => e.etat !== 'IMPOSSIBLE');
                const empechees = apercu.ecartees.filter(e => e.etat === 'IMPOSSIBLE');
                return (
                  <>
                    {reglees.length > 0 && (
                      <div className="kcorr-ecartees">
                        <p>
                          {reglees.length} élément{reglees.length > 1 ? 's' : ''} ne
                          {reglees.length > 1 ? ' sont plus' : " n'est plus"} à traiter — le
                          relevé a vieilli :
                        </p>
                        <ul>
                          {reglees.slice(0, 8).map((e, i) => (
                            <li key={i}><Check size={13} /> {e.raison}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {empechees.length > 0 && (
                      <div className="kcorr-ecartees kcorr-ecartees-bloquees">
                        <p>
                          {empechees.length} élément{empechees.length > 1 ? 's ne peuvent' : ' ne peut'}
                          {' '}pas recevoir ce geste :
                        </p>
                        <ul>
                          {empechees.slice(0, 8).map((e, i) => (
                            <li key={i}><ShieldAlert size={13} /> {e.raison}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </>
                );
              })()}

              {apercu.aCorriger.length === 0 && (
                <p className="kcorr-avis">
                  <CheckCircle2 size={15} />
                  Rien à écrire : tout a déjà été réglé depuis le dernier relevé.
                </p>
              )}
            </>
          )}

          {/* ─── Compte rendu ──────────────────────────────────────────── */}
          {(etape === 'fait' || etape === 'annule') && issue && (
            <>
              {etape === 'annule' ? (
                <p className="kcorr-avis kcorr-avis-ok">
                  <RotateCcw size={15} />
                  {issue.annule.retablies.length} élément
                  {issue.annule.retablies.length > 1 ? 's rétablis' : ' rétabli'} dans son état
                  d'origine.
                  {issue.annule.avertissement ? ` ${issue.annule.avertissement}` : ''}
                </p>
              ) : (
                <p className="kcorr-avis kcorr-avis-ok">
                  <CheckCircle2 size={15} />
                  {(issue.corrigees || []).length > 0
                    ? `${compter(issue.corrigees.length, action.uniteAction)} corrigé${issue.corrigees.length > 1 ? 's' : ''} dans le compte`
                      + (issue.elementsCouverts > issue.corrigees.length && action.uniteElement
                        ? `, ce qui débloque ${compter(issue.elementsCouverts, action.uniteElement)}.`
                        : '.')
                    : 'Rien à écrire : tout était déjà réglé.'}
                </p>
              )}

              {(issue.enEchec || []).length > 0 && (
                <div className="kcorr-echecs">
                  <p><AlertTriangle size={15} /> {issue.enEchec.length} refusé(s) par Google :</p>
                  <ul>
                    {issue.enEchec.map((e, i) => <li key={i}>{e.erreur}</li>)}
                  </ul>
                </div>
              )}
            </>
          )}

          {erreur && (
            <p className="kcorr-avis kcorr-avis-erreur">
              <AlertTriangle size={15} />
              {erreur}
            </p>
          )}
        </div>

        <footer className="kcorr-pied">
          {/* Pendant le saut, un seul geste a du sens : renoncer. */}
          {etape === 'selection' && sautImmediat && (
            <button className="kcorr-bouton" onClick={onFermer}>Annuler</button>
          )}

          {etape === 'selection' && !sautImmediat && (
            <>
              <button className="kcorr-bouton" onClick={onFermer} disabled={occupe}>Annuler</button>
              <button
                className="kcorr-bouton kcorr-bouton-fort"
                /*
                 * ⚠️ **Les parenthèses ne sont pas optionnelles.** Passer la fonction
                 * directement en `onClick` lui livre l'**événement** comme premier argument —
                 * donc comme « élément choisi », dont on lirait ensuite `.cible.adId`. On ferme
                 * la main : le bouton ne choisit rien, il relit la sélection.
                 */
                onClick={() => ouvrirLaSaisie()}
                disabled={occupe || manques.length > 0 || (saisie
                  ? !actionnables.some((_, index) => selection[index])
                  : nombreChoisi === 0)}
              >
                {occupe ? <Loader size={15} className="kcorr-tourne" /> : null}
                {LIBELLE_OUVERTURE[saisie] || `Vérifier ${nombreCibles > 0 ? `(${nombreCibles})` : ''}`}
              </button>
            </>
          )}

          {/* ⚠️ Une analyse ne s'écrit pas : un seul bouton, et il ferme. L'y assortir d'un
              « Corriger » laisserait croire qu'un geste suit. */}
          {etape === 'analyse' && (
            <button className="kcorr-bouton kcorr-bouton-fort" onClick={onFermer}>Fermer</button>
          )}

          {/* ⚠️ Pas d'aperçu serveur pour un flux : il n'y a rien à relire chez Google, et
              l'écran sait déjà ce qu'il va écrire. On va donc droit à l'écriture. */}
          {etape === 'categorie' && (
            <>
              <button className="kcorr-bouton" onClick={onFermer} disabled={occupe}>Annuler</button>
              <button
                className="kcorr-bouton kcorr-bouton-fort"
                onClick={ecrireFlux}
                disabled={occupe || manques.length > 0}
              >
                {occupe ? <Loader size={15} className="kcorr-tourne" /> : null}
                Renseigner dans le flux
              </button>
            </>
          )}

          {(etape === 'accroches' || etape === 'annonce' || etape === 'url' || etape === 'isolement') && (
            <>
              {/* Revenir à une étape qu'on n'a jamais montrée désorienterait : quand le choix a
                  été sauté faute d'alternative, ce bouton ferme. */}
              <button
                className="kcorr-bouton"
                onClick={() => (saut.current ? onFermer() : setEtape('selection'))}
                disabled={occupe}
              >
                {saut.current ? 'Annuler' : 'Revenir'}
              </button>
              <button
                className="kcorr-bouton kcorr-bouton-fort"
                onClick={demanderApercu}
                disabled={occupe || manques.length > 0}
              >
                {occupe ? <Loader size={15} className="kcorr-tourne" /> : null}
                Vérifier
              </button>
            </>
          )}

          {etape === 'redaction' && (
            <>
              {/* Revenir défait la bascule : on retrouve la liste des annonces à réactiver, et
                  non l'écran de création, qu'on n'a jamais demandé. */}
              <button
                className="kcorr-bouton"
                onClick={() => {
                  // Une bascule se défait toujours : on retrouve la liste d'où elle est partie.
                  if (actionBasculee) { setActionBasculee(null); setBrouillon(null); setEtape('selection'); return; }
                  if (saut.current) { onFermer(); return; }
                  setBrouillon(null);
                  setEtape('selection');
                }}
                disabled={occupe}
              >
                {!actionBasculee && saut.current ? 'Annuler' : 'Revenir'}
              </button>
              <button
                className="kcorr-bouton kcorr-bouton-fort"
                onClick={demanderApercu}
                disabled={occupe || manques.length > 0}
              >
                {occupe ? <Loader size={15} className="kcorr-tourne" /> : null}
                Vérifier
              </button>
            </>
          )}

          {etape === 'apercu' && (
            <>
              {/* On revient là d'où l'on vient : corriger un titre depuis l'aperçu ne doit pas
                  obliger à re-choisir le groupe et à reperdre le brouillon. */}
              <button
                className="kcorr-bouton"
                onClick={() => setEtape(ETAPE_DE_SAISIE[saisie] || (saisie ? 'redaction' : 'selection'))}
                disabled={occupe}
              >
                Revenir
              </button>
              <button
                className="kcorr-bouton kcorr-bouton-fort"
                onClick={ecrire}
                disabled={occupe || apercu.aCorriger.length === 0}
              >
                {occupe ? <Loader size={15} className="kcorr-tourne" /> : null}
                {/* Le bouton nomme le geste : « Corriger » ne disait pas ce qui allait partir,
                    et c'est le dernier texte lu avant l'écriture. */}
                {(() => {
                  const verbe = action.verbeBouton || action.verbe || 'modifier';
                  return `${verbe.charAt(0).toUpperCase()}${verbe.slice(1)} dans Google Ads`;
                })()}
              </button>
            </>
          )}

          {etape === 'fait' && (
            <>
              {/* L'annulation reste à portée tant que la modale est ouverte. Au-delà, elle vit
                  dans le journal des corrections — un geste écrit ne disparaît pas d'un
                  rafraîchissement. */}
              {issue.reversible && issue.journalId && (issue.corrigees || []).length > 0 && (
                <button className="kcorr-bouton" onClick={annuler} disabled={occupe}>
                  {occupe ? <Loader size={15} className="kcorr-tourne" /> : <RotateCcw size={15} />}
                  Annuler cette correction
                </button>
              )}
              <button className="kcorr-bouton kcorr-bouton-fort" onClick={onFermer}>Fermer</button>
            </>
          )}

          {etape === 'annule' && (
            <button className="kcorr-bouton kcorr-bouton-fort" onClick={onFermer}>Fermer</button>
          )}
        </footer>
      </div>
    </div>,
    document.body
  );
};

export default ModaleCorrection;
